import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Check,
  Home,
  Image as ImageIcon,
  FileText,
  ShoppingBag,
  Monitor,
  Upload,
  Grid,
  Star,
  Video,
  Layers,
  Heart,
  Mail,
  Plus,
  Trash2,
  Quote,
  Users,
  BookOpen,
  PlayCircle,
  Settings,
  Type,
  Edit,
  Info,
  X,
  CheckCircle,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import EnhancedColorPicker from '../EnhancedColorPicker.jsx';
import EnhancedFontSelector from '../EnhancedFontSelector.jsx';
import Base from '../Base';

// Template Configuration - defines which sections each template needs (matches actual JSON block types)
// Note: Products section removed - products are managed separately via product management
const TEMPLATE_SECTIONS = {
  // Dawn - Clean, Minimalist, Product-focused
  1: ['brand', 'hero', 'categories', 'brand_story', 'lookbook', 'testimonials', 'newsletter', 'instagram', 'business'],
  // Impulse - Bold, energetic design for streetwear/athletic
  2: ['brand', 'announcement', 'hero', 'categories', 'features', 'newsletter', 'instagram', 'business'],
  // Prestige - Luxury, elegant design
  3: ['brand', 'hero', 'collections', 'categories', 'features', 'testimonials', 'newsletter', 'instagram', 'business'],
  // Pipeline - Promotional, deal-focused
  4: ['brand', 'announcement', 'hero', 'flashSale', 'categories', 'videos', 'features', 'newsletter', 'instagram', 'business'],
  // Motion - Dynamic, Gen-Z fashion
  5: ['brand', 'announcement', 'hero', 'categories', 'videos', 'instagram', 'newsletter', 'business'],
  // Symmetry - Editorial, magazine-inspired
  6: ['brand', 'hero', 'categories', 'testimonials', 'newsletter', 'instagram', 'features', 'business'],
  // VOGUE - High-fashion with designers
  7: ['brand', 'hero', 'categories', 'designers', 'lookbook', 'testimonials', 'newsletter', 'instagram', 'services', 'business'],
  // REFORMATION - Sustainability-focused
  8: ['brand', 'hero', 'impactStats', 'categories', 'mission', 'artisans', 'testimonials', 'certifications', 'newsletter', 'instagram', 'services', 'business'],
  // STILETTO - Glamorous womens fashion
  9: ['brand', 'hero', 'intro', 'categories', 'lookbook', 'features', 'testimonials', 'newsletter', 'instagram', 'services', 'business'],
  // MONO - Clean minimalist
  10: ['brand', 'hero', 'editorial', 'philosophy', 'categories', 'quote', 'testimonials', 'newsletter', 'instagram', 'services', 'business'],
  // ALLURE - Premium menswear
  11: ['brand', 'hero', 'categories', 'styleGuide', 'bespoke', 'testimonials', 'newsletter', 'instagram', 'services', 'business'],
  // Venue - Festival and travel-inspired
  12: ['brand', 'hero', 'marquee', 'categories', 'cityCollections', 'testimonials', 'newsletter', 'instagram', 'services', 'business'],
  // Craft - Artisan-focused
  13: ['brand', 'hero', 'categories', 'craftsmanship', 'newsletter', 'business'],
  // Studio - Creative portfolio
  14: ['brand', 'hero', 'portfolio', 'quote', 'behindScenes', 'testimonials', 'newsletter', 'instagram', 'services', 'business'],
  // Sahara - Earthy, bohemian
  15: ['brand', 'announcement', 'hero', 'collections', 'philosophy', 'editorial', 'testimonials', 'newsletter', 'business'],
  // Aurora - Luxury fashion house
  16: ['brand', 'announcement', 'hero', 'categories', 'videoLookbook', 'consultation', 'testimonials', 'stores', 'newsletter', 'business'],
  // BLUM - Modern elegant
  17: ['brand', 'announcement', 'hero', 'lookbook', 'videoBanner', 'philosophy', 'instagram', 'newsletter', 'services', 'business'],
  // AVANTE - Avant-garde editorial
  18: ['brand', 'announcement', 'hero', 'categories', 'videoCampaign', 'testimonials', 'newsletter', 'services', 'business'],
  // ZEST - Youthful, energetic
  19: ['brand', 'announcement', 'hero', 'features', 'categories', 'video', 'reviews', 'newsletter', 'services', 'business'],
  // SUPREME - Multi-brand marketplace
  20: ['brand', 'announcement', 'hero', 'services', 'categories', 'featuredBrands', 'trustBanner', 'reviews', 'newsletter', 'business']
};

// Section metadata (matches JSON content keys only)
const SECTION_METADATA = {
  // Core sections (all templates)
  brand: { label: 'Brand Information', icon: ShoppingBag },
  hero: { label: 'Hero Section', icon: Monitor },
  categories: { label: 'Categories', icon: Grid },
  products: { label: 'Products', icon: ShoppingBag },
  newsletter: { label: 'Newsletter', icon: Mail },
  business: { label: 'Business Information', icon: FileText },

  // Announcement / Promo bars
  announcement: { label: 'Announcement Bar', icon: Info },
  promo: { label: 'Promo Banner', icon: Info },
  marquee: { label: 'Marquee Banner', icon: Type },

  // Content sections
  intro: { label: 'Introduction', icon: Quote },
  collections: { label: 'Collections', icon: Layers },
  lookbook: { label: 'Lookbook', icon: BookOpen },
  instagram: { label: 'Instagram Feed', icon: ImageIcon },
  testimonials: { label: 'Testimonials', icon: Users },
  testimonial: { label: 'Testimonial', icon: Quote },
  quote: { label: 'Quote', icon: Quote },

  // Brand story sections
  brand_story: { label: 'Brand Story', icon: BookOpen },
  brandStory: { label: 'Brand Story', icon: BookOpen },
  brandStatement: { label: 'Brand Statement', icon: Quote },
  philosophy: { label: 'Philosophy', icon: Heart },
  mission: { label: 'Mission', icon: Heart },

  // Feature sections
  features: { label: 'Features', icon: Star },
  services: { label: 'Services', icon: Settings },
  servicesBar: { label: 'Services Bar', icon: Settings },

  // Athlete / Brand Values (Template 2)
  athlete: { label: 'Athlete Showcase', icon: Star },
  brandValues: { label: 'Brand Values', icon: Heart },

  // Editorial / Content (Template 3, 6, 15)
  editorial: { label: 'Editorial', icon: FileText },

  // Flash Sale / Trending (Template 4, 5)
  flashSale: { label: 'Flash Sale', icon: Sparkles },
  trending: { label: 'Trending', icon: Star },
  promoBanners: { label: 'Promo Banners', icon: ImageIcon },

  // Video sections
  videos: { label: 'Videos', icon: Video },
  video: { label: 'Video', icon: PlayCircle },
  reels: { label: 'Reels', icon: Video },
  videoBanner: { label: 'Video Banner', icon: Video },
  videoCampaign: { label: 'Video Campaign', icon: Video },
  videoLookbook: { label: 'Video Lookbook', icon: Video },

  // Social sections
  ugc: { label: 'User Generated Content', icon: Users },

  // Craftsmanship / Artisan (Template 6, 8)
  craftsmanship: { label: 'Craftsmanship', icon: Settings },
  artisans: { label: 'Artisans', icon: Users },
  transparency: { label: 'Transparency', icon: Info },
  certifications: { label: 'Certifications', icon: Star },
  impactStats: { label: 'Impact Stats', icon: Star },

  // Designers (Template 7)
  designers: { label: 'Designers', icon: Users },

  // Featured / Materials (Template 10)
  featuredImage: { label: 'Featured Image', icon: ImageIcon },
  materials: { label: 'Materials', icon: Layers },

  // Style Guide / Bespoke (Template 11)
  styleGuide: { label: 'Style Guide', icon: BookOpen },
  bespoke: { label: 'Bespoke Services', icon: Star },

  // City Collections (Template 12)
  cityCollections: { label: 'City Collections', icon: Grid },

  // Portfolio / Behind Scenes (Template 14)
  portfolio: { label: 'Portfolio', icon: ImageIcon },
  behindScenes: { label: 'Behind the Scenes', icon: PlayCircle },

  // Consultation / Stores (Template 16)
  consultation: { label: 'Consultation', icon: Users },
  stores: { label: 'Store Locations', icon: Grid },

  // The Edit (Template 18)
  theEdit: { label: 'The Edit', icon: Edit },

  // Reviews (Template 19, 20)
  reviews: { label: 'Reviews', icon: Star },

  // Featured Brands / Trust (Template 20)
  featuredBrands: { label: 'Featured Brands', icon: Layers },
  trustBanner: { label: 'Trust Banner', icon: Star }
};

const MainPageForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeSection, setActiveSection] = useState('brand');

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});

  // Theme state - stores original theme from the template JSON
  const [themeData, setThemeData] = useState(null);

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    // Core brand info
    brand: {
      name: 'Your Brand Name',
      logo: '',
      tagline: 'Crafting Style, Defining Elegance',
      description: 'Premium fashion brand committed to quality and style since 2020.'
    },

    // Hero section (various formats per template)
    hero: {
      title: 'Discover Your Style',
      titleLine1: 'New Season',
      titleLine2: 'Collection',
      subtitle: 'Explore our latest collection of premium fashion pieces designed for the modern lifestyle.',
      description: 'Handcrafted with passion, designed for elegance.',
      label: 'New Arrival',
      badge: 'New Collection 2024',
      background_image: '',
      backgroundImage: '',
      image: '',
      posterImage: '',
      videoSrc: '',
      cta_text: 'Shop Collection',
      cta_link: '/shop',
      ctaText: 'Shop Collection',
      ctaLink: '/shop',
      ctaPrimary: 'Shop Now',
      ctaSecondary: 'Learn More',
      secondaryCtaText: 'View Lookbook',
      secondaryCtaLink: '/lookbook',
      slides: [
        { image: '', title: 'Summer Collection', subtitle: 'Light & Breezy Styles', badge: 'New', cta: 'Shop Now' },
        { image: '', title: 'Autumn Essentials', subtitle: 'Warm & Cozy Pieces', badge: 'Trending', cta: 'Explore' }
      ],
      collections: [{ name: 'Summer 2024', image: '' }, { name: 'Autumn Classics', image: '' }],
      stats: [{ value: '10K+', label: 'Happy Customers' }, { value: '500+', label: 'Products' }],
      floatingBadge: { label: 'Quality', value: 'Premium' },
      floatingStats: { value: '4.9', label: 'Rating' },
      floatingTag: 'Bestseller',
      images: [],
      impactLabel: 'Our Impact',
      quickStats: [{ value: '50%', label: 'Less Water' }, { value: '100%', label: 'Organic' }],
      badge1Label: 'Certified',
      badge1Value: 'Organic',
      certBadge: 'GOTS Certified'
    },

    // Announcement / Promo sections
    announcement: { text: 'Free Shipping on Orders Over ₹999', linkText: 'Shop Now' },
    promo: { message: 'Flash Sale! Up to 50% Off', code: 'Use Code: FLASH50' },
    marquee: { text: 'NEW ARRIVALS • FREE SHIPPING • PREMIUM QUALITY • SUSTAINABLE FASHION', words: ['NEW ARRIVALS', 'FREE SHIPPING', 'PREMIUM QUALITY'] },

    // Categories (object with items array)
    categories: {
      label: 'Shop By Category',
      title: 'Our Collections',
      subtitle: 'Find your perfect style',
      description: 'Explore our curated categories designed for every occasion.',
      items: [
        { name: 'Women', count: '120+ Items', description: 'Elegant styles for her', image: '', tag: 'Popular', icon: 'Shirt', tagline: 'Timeless Elegance' },
        { name: 'Men', count: '95+ Items', description: 'Classic styles for him', image: '', tag: 'New', icon: 'User', tagline: 'Modern Sophistication' },
        { name: 'Accessories', count: '50+ Items', description: 'Complete your look', image: '', tag: 'Trending', icon: 'Watch', tagline: 'Finishing Touches' }
      ]
    },

    // Products section
    products: {
      label: 'Featured',
      title: 'Best Sellers',
      badge: 'Top Picks',
      section_title: 'Our Products',
      section_subtitle: 'Handpicked for you',
      view_all_link: '/shop',
      ctaText: 'View All Products',
      filters: [{ id: 'all', name: 'All' }, { id: 'new', name: 'New Arrivals' }, { id: 'sale', name: 'On Sale' }]
    },

    // Collections (object with items array)
    collections: {
      label: 'Curated',
      title: 'Featured Collections',
      items: [
        { name: 'Summer Essentials', tagline: 'Light & Breezy', count: '24 Items', description: 'Perfect for sunny days', image: '' },
        { name: 'Office Wear', tagline: 'Professional Style', count: '32 Items', description: 'Elevate your work wardrobe', image: '' }
      ]
    },

    // Lookbook section
    lookbook: {
      label: 'Inspiration',
      title: 'The Lookbook',
      subtitle: 'Get inspired by our latest styles',
      ctaText: 'View Full Lookbook',
      items: [
        { image: '', title: 'Urban Chic' },
        { image: '', title: 'Weekend Vibes' },
        { image: '', title: 'Evening Elegance' }
      ],
      images: []
    },

    // Instagram feed
    instagram: {
      handle: '@yourbrand',
      title: 'Follow Us on Instagram',
      hashtag: '#YourBrandStyle',
      images: [{ src: '' }, { src: '' }, { src: '' }, { src: '' }],
      posts: [
        { image: '', likes: '1.2K' },
        { image: '', likes: '856' },
        { image: '', likes: '2.1K' }
      ]
    },

    // Introduction / Quote sections
    intro: {
      quote: 'Where tradition meets contemporary design, creating pieces that tell your story.',
      description: 'We believe fashion is more than clothing—it\'s self-expression.',
      attribution: '— The Founder',
      badge: 'Our Philosophy'
    },
    quote: {
      text: 'Fashion is the armor to survive the reality of everyday life.',
      author: 'Bill Cunningham',
      highlight: 'Fashion is the armor',
      textEnd: 'to survive the reality of everyday life.',
      attribution: 'Fashion Icon'
    },

    // Testimonials section (various formats)
    testimonials: {
      label: 'What Our Customers Say',
      title: 'Customer Stories',
      items: [
        { text: 'Absolutely love the quality! The fabric feels luxurious and the fit is perfect.', name: 'Priya Sharma', location: 'Mumbai', city: 'Mumbai', initial: 'P', rating: 5, role: 'Fashion Blogger', event: '', image: '' },
        { text: 'Best online shopping experience. Fast delivery and amazing customer service.', name: 'Rahul Verma', location: 'Delhi', city: 'Delhi', initial: 'R', rating: 5, role: 'Verified Buyer', event: '', image: '' },
        { text: 'The attention to detail is remarkable. Will definitely order again!', name: 'Anita Patel', location: 'Bangalore', city: 'Bangalore', initial: 'A', rating: 5, role: 'Loyal Customer', event: '', image: '' }
      ]
    },
    testimonial: {
      quote: 'This brand has completely transformed my wardrobe. Every piece is a statement.',
      author: 'Sneha Kapoor',
      title: 'Style Influencer'
    },

    // Reviews section
    reviews: {
      label: 'Reviews',
      title: 'What Customers Are Saying',
      items: [
        { text: 'Premium quality at great prices. Highly recommended!', name: 'Amit Kumar', city: 'Chennai', initial: 'A', rating: 5 },
        { text: 'Fast shipping and the clothes fit perfectly.', name: 'Neha Singh', city: 'Pune', initial: 'N', rating: 5 },
        { text: 'Excellent customer service and beautiful designs.', name: 'Vikram Rao', city: 'Hyderabad', initial: 'V', rating: 4 }
      ]
    },

    // Newsletter section
    newsletter: {
      label: 'Stay Connected',
      title: 'Join Our Newsletter',
      subtitle: 'Be the first to know about new collections and exclusive offers.',
      description: 'Subscribe and get 10% off your first order.',
      placeholder: 'Enter your email address',
      button_text: 'Subscribe',
      buttonText: 'Subscribe',
      disclaimer: 'By subscribing, you agree to our Privacy Policy.'
    },

    // Brand story sections
    brand_story: {
      subtitle: 'Our Journey',
      title: 'Crafting Excellence Since 2015',
      description: 'What started as a small passion project has grown into a beloved fashion brand, trusted by thousands across India.',
      secondary_description: 'Every stitch tells a story of dedication and craftsmanship.',
      secondaryText: 'We work directly with artisans to bring you authentic, quality pieces.',
      paragraph1: 'Our journey began with a simple belief: that everyone deserves access to beautiful, well-made clothing.',
      paragraph2: 'Today, we continue that mission with every collection we create.',
      image: '',
      cta_text: 'Learn More',
      cta_link: '/about',
      ctaText: 'Our Story',
      ctaLink: '/about',
      stats: [{ value: '10+', label: 'Years Experience' }, { value: '50K+', label: 'Happy Customers' }, { value: '100%', label: 'Satisfaction' }]
    },
    brandStory: {
      label: 'Our Heritage',
      title: 'A Legacy of Style',
      description: 'For over a decade, we have been redefining fashion with our commitment to quality and innovation.',
      secondaryText: 'Each piece is crafted with meticulous attention to detail.',
      paragraph1: 'From our humble beginnings to becoming a household name.',
      paragraph2: 'Our passion for fashion drives everything we do.',
      image: '',
      ctaText: 'Discover More',
      ctaLink: '/about',
      stats: [{ value: '15+', label: 'Years' }, { value: '100+', label: 'Artisans' }]
    },
    brandStatement: {
      quote: 'We believe in creating fashion that empowers and inspires.',
      description: 'Our commitment to excellence drives every design decision we make.'
    },

    // Philosophy / Mission sections
    philosophy: {
      label: 'Our Philosophy',
      title: 'Timeless Design, Conscious Creation',
      description: 'We create fashion that transcends trends, focusing on timeless pieces that become wardrobe staples.',
      description1: 'Quality over quantity is our mantra.',
      description2: 'We believe in slow fashion that lasts.',
      ctaText: 'Learn More',
      image: '',
      quote: 'True style is timeless.'
    },
    mission: {
      quote: 'To create sustainable fashion that doesn\'t compromise on style or quality.',
      description: 'We are committed to ethical practices and environmental responsibility in every step of our process.'
    },

    // Editorial section
    editorial: {
      label: 'The Journal',
      title: 'Style Stories',
      subtitle: 'Inspiration & Trends',
      description: 'Discover the latest in fashion, styling tips, and behind-the-scenes stories.',
      backgroundImage: '',
      ctaText: 'Read More',
      ctaLink: '/journal',
      image: '',
      items: [
        { title: 'Summer Style Guide 2024', category: 'Trends', image: '' },
        { title: 'How to Build a Capsule Wardrobe', category: 'Tips', image: '' },
        { title: 'Meet Our Artisans', category: 'Behind the Scenes', image: '' }
      ]
    },

    // Features section
    features: {
      title: 'Why Choose Us',
      items: [
        { icon: 'Truck', title: 'Free Shipping', description: 'On orders above ₹999' },
        { icon: 'RotateCcw', title: 'Easy Returns', description: '30-day hassle-free returns' },
        { icon: 'Shield', title: 'Secure Payment', description: '100% secure checkout' },
        { icon: 'Headphones', title: '24/7 Support', description: 'Always here to help' }
      ]
    },

    // Services section
    services: {
      label: 'Our Services',
      title: 'What We Offer',
      items: [
        { icon: 'Scissors', title: 'Custom Tailoring', description: 'Perfect fit guaranteed', subtitle: 'Personalized service' },
        { icon: 'Gift', title: 'Gift Wrapping', description: 'Beautiful presentation', subtitle: 'Complimentary' },
        { icon: 'Shirt', title: 'Personal Styling', description: 'Expert fashion advice', subtitle: 'Book a session' }
      ]
    },
    servicesBar: [
      { icon: 'Truck', title: 'Free Delivery', description: 'On orders ₹999+' },
      { icon: 'RotateCcw', title: 'Easy Returns', description: '30 Days' },
      { icon: 'Shield', title: 'Secure Payment', description: '100% Safe' }
    ],

    // Athlete showcase (Template 2)
    athlete: {
      label: 'Performance Wear',
      quote: 'Push your limits with gear designed for champions.',
      description: 'Our performance collection is engineered for those who demand excellence.',
      backgroundImage: '',
      ctaText: 'Shop Performance',
      ctaLink: '/performance'
    },

    // Brand Values (Template 2)
    brandValues: {
      label: 'Our Values',
      title: 'What Drives Us',
      description: 'Quality, sustainability, and innovation are at the heart of everything we create.',
      images: [{ src: '', alt: 'Quality Materials' }, { src: '', alt: 'Sustainable Practices' }],
      points: ['Premium Materials', 'Ethical Production', 'Sustainable Packaging', 'Fair Trade'],
      ctaText: 'Learn More',
      ctaLink: '/values'
    },

    // Flash Sale (Template 4)
    flashSale: {
      title: 'Flash Sale',
      subtitle: 'Up to 70% Off - Limited Time Only!',
      endTime: '2024-12-31T23:59:59',
      ctaText: 'Shop Sale'
    },

    // Promo banners (Template 4)
    promoBanners: [
      { image: '', title: 'Summer Sale - Up to 50% Off', ctaText: 'Shop Now' },
      { image: '', title: 'New Arrivals', ctaText: 'Explore' }
    ],

    // Videos section
    videos: {
      title: 'Watch & Shop',
      subtitle: 'See our collections in action',
      items: [
        { url: '', title: 'Summer Collection 2024', description: 'Behind the scenes of our latest photoshoot', thumbnail: '' },
        { url: '', title: 'Styling Tips', description: 'How to style our bestsellers', thumbnail: '' }
      ]
    },
    video: {
      label: 'Featured Video',
      title: 'The Making Of',
      thumbnail: '',
      url: '',
      image: '',
      videoUrl: ''
    },
    videoBanner: {
      label: 'Watch Now',
      title: 'Spring Summer 2024',
      image: '',
      videoUrl: ''
    },
    videoCampaign: {
      label: 'Campaign',
      title: 'The New Chapter',
      image: '',
      videoUrl: ''
    },
    videoLookbook: {
      label: 'Video Lookbook',
      title: 'Autumn Winter Collection',
      image: '',
      videoUrl: ''
    },

    // Trending (Template 5)
    trending: {
      title: 'Trending Now',
      items: [
        { name: 'Oversized Blazer', price: '₹2,999', tag: 'Hot', image: '' },
        { name: 'Silk Scarf', price: '₹1,499', tag: 'New', image: '' },
        { name: 'Leather Tote', price: '₹3,999', tag: 'Bestseller', image: '' }
      ]
    },

    // Reels (Template 5)
    reels: {
      title: 'Shop The Reels',
      items: [
        { image: '', views: '12.5K', likes: '2.3K' },
        { image: '', views: '8.9K', likes: '1.8K' },
        { image: '', views: '15.2K', likes: '3.1K' }
      ]
    },

    // UGC section (Template 5)
    ugc: {
      hashtag: '#MyBrandStyle',
      subtitle: 'Share your style with us',
      images: []
    },

    // Craftsmanship (Template 6, 8)
    craftsmanship: {
      label: 'Artisanal Excellence',
      title: 'The Art of Craftsmanship',
      description: 'Every piece is handcrafted by skilled artisans who have perfected their craft over generations.',
      features: ['Hand-stitched Details', 'Premium Materials', 'Quality Checked'],
      images: [],
      ctaText: 'Our Process',
      ctaLink: '/craftsmanship',
      stats: [{ value: '100+', label: 'Artisans' }, { value: '50+', label: 'Years Combined Experience' }]
    },

    // Impact Stats (Template 8)
    impactStats: {
      title: 'Our Environmental Impact',
      items: [
        { icon: 'Droplet', value: '50%', label: 'Less Water Used', description: 'Compared to conventional methods' },
        { icon: 'Leaf', value: '100%', label: 'Organic Cotton', description: 'GOTS certified materials' },
        { icon: 'Recycle', value: '0', label: 'Plastic Packaging', description: 'Fully biodegradable packaging' },
        { icon: 'Heart', value: '1000+', label: 'Artisan Families', description: 'Supported through fair trade' }
      ]
    },

    // Artisans (Template 8)
    artisans: {
      title: 'Meet Our Artisans',
      description: 'The skilled hands behind every piece we create.',
      items: [
        { name: 'Ramesh Kumar', craft: 'Hand Weaving', location: 'Varanasi', story: 'Third-generation weaver keeping traditional techniques alive.', image: '' },
        { name: 'Lakshmi Devi', craft: 'Block Printing', location: 'Jaipur', story: 'Creating intricate patterns using 200-year-old wooden blocks.', image: '' },
        { name: 'Mohammed Ali', craft: 'Embroidery', location: 'Lucknow', story: 'Master of Chikankari embroidery with 25 years experience.', image: '' }
      ]
    },

    // Transparency (Template 8)
    transparency: {
      title: 'Our Transparency Promise',
      description: 'We believe you deserve to know exactly where your clothes come from and how they\'re made.',
      features: ['Full Supply Chain Visibility', 'Fair Wage Guarantee', 'Environmental Impact Reports'],
      ctaText: 'View Our Impact Report',
      images: [{ src: '', alt: 'Our Factory' }, { src: '', alt: 'Our Materials' }]
    },

    // Certifications (Template 8)
    certifications: [
      { name: 'GOTS Certified', description: 'Global Organic Textile Standard' },
      { name: 'Fair Trade', description: 'Fair wages and working conditions' },
      { name: 'B Corp', description: 'Meeting highest social and environmental standards' }
    ],

    // Designers (Template 7)
    designers: {
      title: 'Our Designers',
      items: [
        { name: 'Anjali Mehta', specialty: 'Contemporary Fusion', image: '' },
        { name: 'Vikram Sharma', specialty: 'Minimalist Design', image: '' },
        { name: 'Priya Nair', specialty: 'Traditional Revival', image: '' }
      ]
    },

    // Featured Image (Template 10)
    featuredImage: { src: '', alt: 'Featured Collection - Minimalist Elegance' },

    // Materials (Template 10)
    materials: {
      title: 'Premium Materials',
      description: 'We source only the finest materials from around the world, ensuring every piece meets our exacting standards.',
      features: ['100% Organic Cotton', 'Mulberry Silk', 'Italian Leather'],
      image1: '',
      image2: ''
    },

    // Style Guide (Template 11)
    styleGuide: {
      title: 'Style Guide',
      items: [
        { title: 'Business Casual', description: 'Elevate your work wardrobe with these versatile pieces.', image: '' },
        { title: 'Weekend Ready', description: 'Relaxed yet refined looks for your days off.', image: '' },
        { title: 'Evening Elegance', description: 'Make a statement at your next special occasion.', image: '' }
      ]
    },

    // Bespoke (Template 11)
    bespoke: {
      title: 'Bespoke Services',
      description: 'Experience the luxury of made-to-measure fashion. Our expert tailors create pieces that fit you perfectly.',
      features: ['Personal Consultation', 'Custom Measurements', 'Premium Fabrics'],
      ctaText: 'Book Appointment',
      images: []
    },

    // City Collections (Template 12)
    cityCollections: {
      label: 'City Edit',
      title: 'Collections By City',
      items: [
        { city: 'Mumbai', vibe: 'Urban Chic', image: '' },
        { city: 'Delhi', vibe: 'Royal Elegance', image: '' },
        { city: 'Bangalore', vibe: 'Tech Casual', image: '' }
      ]
    },

    // Portfolio (Template 14)
    portfolio: {
      label: 'Our Work',
      title: 'Portfolio',
      subtitle: 'A showcase of our finest creations',
      items: [
        { title: 'Bridal Collection 2024', category: 'Bridal', image: '', aspect: 'portrait' },
        { title: 'Resort Wear', category: 'Casual', image: '', aspect: 'landscape' },
        { title: 'Festive Edit', category: 'Occasion', image: '', aspect: 'square' }
      ]
    },

    // Behind Scenes (Template 14)
    behindScenes: {
      label: 'Behind The Scenes',
      title: 'Our Creative Process',
      description: 'Take a peek into how we bring our designs to life.',
      subdescription: 'From sketch to stitch, every step matters.',
      ctaText: 'Learn More',
      items: [
        { title: 'Design & Sketching', image: '' },
        { title: 'Fabric Selection', image: '' },
        { title: 'Pattern Making', image: '' },
        { title: 'Final Touches', image: '' }
      ]
    },

    // Consultation (Template 16)
    consultation: {
      label: 'Personal Styling',
      title: 'Book a Consultation',
      description: 'Get personalized styling advice from our expert fashion consultants.',
      features: ['One-on-one session', 'Wardrobe analysis', 'Personalized recommendations'],
      ctaText: 'Book Now',
      image: ''
    },

    // Stores (Template 16)
    stores: {
      label: 'Visit Us',
      title: 'Our Stores',
      items: [
        { city: 'Mumbai', address: 'Linking Road, Bandra West', phone: '+91 22 1234 5678' },
        { city: 'Delhi', address: 'Khan Market', phone: '+91 11 1234 5678' },
        { city: 'Bangalore', address: 'Indiranagar 100ft Road', phone: '+91 80 1234 5678' }
      ]
    },

    // The Edit (Template 18)
    theEdit: {
      label: 'Curated',
      title: 'The Edit',
      description: 'Our editors\' top picks for the season.',
      ctaText: 'Shop The Edit',
      image: ''
    },

    // Featured Brands (Template 20)
    featuredBrands: {
      label: 'Our Partners',
      title: 'Featured Brands',
      items: [
        { name: 'Artisan Collective', products: '45+ Products', image: '' },
        { name: 'Sustainable Threads', products: '30+ Products', image: '' },
        { name: 'Heritage Weaves', products: '25+ Products', image: '' }
      ]
    },

    // Trust Banner (Template 20)
    trustBanner: {
      title: 'Trusted by 50,000+ Customers',
      description: 'Join our community of style-conscious shoppers who trust us for quality and value.'
    },

    // Footer section
    footer: {
      shopLinks: [
        { label: 'New Arrivals', url: '/new' },
        { label: 'Bestsellers', url: '/bestsellers' },
        { label: 'Sale', url: '/sale' }
      ],
      helpLinks: [
        { label: 'Contact Us', url: '/contact' },
        { label: 'FAQs', url: '/faq' },
        { label: 'Size Guide', url: '/size-guide' }
      ],
      copyright: '© 2024 Your Brand. All rights reserved.'
    },

    // Business information
    business: {
      contact: {
        business_name: 'Your Brand Name',
        email: 'hello@yourbrand.com',
        phone: '+91 98765 43210',
        address: '123 Fashion Street, Mumbai, Maharashtra 400001',
        working_hours: 'Mon-Sat: 10AM-8PM'
      },
      hours: 'Monday to Saturday: 10:00 AM - 8:00 PM'
    },

    // Social links
    social: {
      instagram: 'https://instagram.com/yourbrand',
      facebook: 'https://facebook.com/yourbrand',
      twitter: 'https://twitter.com/yourbrand',
      message: 'Follow us for the latest updates and exclusive offers!',
      links: []
    }
  });

  // Get sections for current template
  const templateSections = TEMPLATE_SECTIONS[parseInt(templateId)] || ['brand', 'hero', 'business'];
  const sections = templateSections.map(sectionId => ({
    id: sectionId,
    ...SECTION_METADATA[sectionId]
  }));

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  useEffect(() => {
    // Set first available section as active
    if (sections.length > 0) {
      setActiveSection(sections[0].id);
    }
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/template/${templateId}/page/main`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Loaded page data response:', response.data);

      // Check if there's saved data
      if (response.data.success && response.data.data) {
        const savedData = response.data.data;

        // Extract theme data if available and set it in formData.theme
        if (savedData.theme) {
          console.log('Loading theme data:', savedData.theme);
          setThemeData(savedData.theme);

          // Convert theme to formData format for color pickers
          const themeForForm = {
            colors: {
              primary: Array.isArray(savedData.theme.colors?.primary)
                ? savedData.theme.colors.primary[0]
                : savedData.theme.colors?.primary || '#2C1810',
              secondary: savedData.theme.colors?.secondary || '#ffffff',
              accent: savedData.theme.colors?.accent || '#C9A882',
              background: savedData.theme.colors?.background || '#ffffff',
              text: savedData.theme.colors?.text?.primary || savedData.theme.colors?.text || '#111827',
              surface: savedData.theme.colors?.surface || '#ffffff',
              border: savedData.theme.colors?.border || '#E5E7EB'
            },
            typography: {
              heading: savedData.theme.typography?.heading || 'sans-serif',
              body: savedData.theme.typography?.body || 'sans-serif'
            }
          };

          setFormData(prev => ({
            ...prev,
            theme: themeForForm
          }));
        }

        // If it's v2.0 format JSON, extract the content
        if (savedData.version === '2.0' && savedData.content) {
          console.log('Loading v2.0 format data');
          setFormData(prev => normalizeFormData({ ...prev, ...savedData.content }));
        }
        // If it's old format or direct content
        else if (savedData.content) {
          console.log('Loading content data');
          setFormData(prev => normalizeFormData({ ...prev, ...savedData.content }));
        }
        // If the whole saved data is the form data
        else {
          console.log('Loading saved data directly');
          setFormData(prev => normalizeFormData({ ...prev, ...savedData }));
        }
      }
      // Fallback to old response format
      else if (response.data.content) {
        console.log('Loading old format content');
        setFormData(prev => normalizeFormData({ ...prev, ...response.data.content }));
        // Also check for theme in old format
        if (response.data.theme) {
          setThemeData(response.data.theme);
          // Convert theme to formData format
          const themeForForm = {
            colors: {
              primary: Array.isArray(response.data.theme.colors?.primary)
                ? response.data.theme.colors.primary[0]
                : response.data.theme.colors?.primary || '#2C1810',
              secondary: response.data.theme.colors?.secondary || '#ffffff',
              accent: response.data.theme.colors?.accent || '#C9A882',
              background: response.data.theme.colors?.background || '#ffffff',
              text: response.data.theme.colors?.text?.primary || response.data.theme.colors?.text || '#111827'
            },
            typography: {
              heading: response.data.theme.typography?.heading || 'sans-serif',
              body: response.data.theme.typography?.body || 'sans-serif'
            }
          };
          setFormData(prev => ({ ...prev, theme: themeForForm }));
        }
      }
    } catch (error) {
      console.error('Error loading main page data:', error);
      // Don't show error for 404 - it just means no data saved yet
      if (error.response && error.response.status !== 404) {
        console.error('Unexpected error:', error.response);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);

      // Get authentication token
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to save changes');
        navigate('/login');
        return;
      }

      // Check if there are any uploaded files
      const hasFiles = Object.keys(uploadedFiles).length > 0;

      // Normalize field names before saving (ensures both camelCase and snake_case variants exist)
      const normalizedData = normalizeFormData(formData);

      if (hasFiles) {
        // Use FormData for multipart/form-data when files are present
        const formDataToSend = new FormData();
        formDataToSend.append('status', isDraft ? 'draft' : 'complete');
        formDataToSend.append('content', JSON.stringify(normalizedData));

        // Append all uploaded files
        Object.entries(uploadedFiles).forEach(([fieldName, file]) => {
          formDataToSend.append(fieldName, file);
        });

        await axios.post(`/api/template/${templateId}/page/main/save`, formDataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Use JSON when no files
        await axios.post(`/api/template/${templateId}/page/main/save`, {
          status: isDraft ? 'draft' : 'complete',
          content: normalizedData
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      setLastSaved(new Date());

      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);

      // Extract detailed error message
      let errMsg = 'Error saving changes. Please try again.';
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);

        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errMsg = error.response.data;
          } else if (error.response.data.error) {
            errMsg = error.response.data.error;
          } else if (error.response.data.message) {
            errMsg = error.response.data.message;
          } else if (error.response.data.detail) {
            errMsg = error.response.data.detail;
          }
        }
      } else if (error.request) {
        errMsg = 'No response from server. Please check your connection.';
      } else {
        errMsg = error.message || errMsg;
      }

      setErrorMessage(errMsg);
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close and navigation
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addArrayItem = (arrayName, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), defaultItem]
    }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  // Nested array helpers (for arrays inside objects, e.g. brand_story.stats)
  const addNestedArrayItem = (parentKey, arrayKey, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [arrayKey]: [...((prev[parentKey] || {})[arrayKey] || []), defaultItem]
      }
    }));
  };

  const updateNestedArrayItem = (parentKey, arrayKey, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [arrayKey]: ((prev[parentKey] || {})[arrayKey] || []).map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const removeNestedArrayItem = (parentKey, arrayKey, index) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...(prev[parentKey] || {}),
        [arrayKey]: ((prev[parentKey] || {})[arrayKey] || []).filter((_, i) => i !== index)
      }
    }));
  };

  // Normalize form data to ensure both field name variants exist for template compatibility
  // Some templates use camelCase (e.g., backgroundImage), others use snake_case (e.g., background_image)
  // Some templates use "name" for categories, others use "title" — we save both so all templates render correctly
  const normalizeFormData = (data) => {
    const normalized = { ...data };

    // Hero: sync backgroundImage <-> background_image, ctaText <-> cta_text, ctaLink <-> cta_link
    if (normalized.hero) {
      normalized.hero = { ...normalized.hero };
      const bgImg = normalized.hero.background_image || normalized.hero.backgroundImage || '';
      normalized.hero.background_image = bgImg;
      normalized.hero.backgroundImage = bgImg;

      const ctaText = normalized.hero.cta_text || normalized.hero.ctaText || '';
      normalized.hero.cta_text = ctaText;
      normalized.hero.ctaText = ctaText;

      const ctaLink = normalized.hero.cta_link || normalized.hero.ctaLink || '';
      normalized.hero.cta_link = ctaLink;
      normalized.hero.ctaLink = ctaLink;
    }

    // Categories: sync name <-> title
    if (Array.isArray(normalized.categories)) {
      normalized.categories = normalized.categories.map(cat => ({
        ...cat,
        name: cat.name || cat.title || '',
        title: cat.title || cat.name || ''
      }));
    }

    // Collections: sync name <-> title
    if (Array.isArray(normalized.collections)) {
      normalized.collections = normalized.collections.map(col => ({
        ...col,
        name: col.name || col.title || '',
        title: col.title || col.name || ''
      }));
    }

    // Fashion trends: sync name <-> text
    if (Array.isArray(normalized.fashion_trends)) {
      normalized.fashion_trends = normalized.fashion_trends.map(trend => ({
        ...trend,
        name: trend.name || trend.text || '',
        text: trend.text || trend.name || ''
      }));
    }

    // Statement: sync backgroundImage <-> background_image
    if (normalized.statement) {
      normalized.statement = { ...normalized.statement };
      const bgImg = normalized.statement.background_image || normalized.statement.backgroundImage || '';
      normalized.statement.background_image = bgImg;
      normalized.statement.backgroundImage = bgImg;
    }

    return normalized;
  };

  const handleFileUpload = (fieldName, file) => {
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImages(prev => ({
            ...prev,
            [fieldName]: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Helper function to get YouTube/Vimeo embed URL
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;

    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return null;
  };

  // Video Preview Component
  const VideoPreview = ({ url, onRemove }) => {
    const embedUrl = getVideoEmbedUrl(url);

    if (!url) return null;

    return (
      <div className="mt-4 relative">
        {embedUrl ? (
          // YouTube/Vimeo embed
          <div className="aspect-video rounded-lg overflow-hidden border-2 border-gray-200">
            <iframe
              src={embedUrl}
              title="Video preview"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : url.startsWith('http') || url.startsWith('/media') ? (
          // Direct video URL
          <video
            src={url}
            controls
            className="w-full rounded-lg border-2 border-gray-200"
            style={{ maxHeight: '300px' }}
          >
            Your browser does not support the video tag.
          </video>
        ) : null}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  // Aspect ratio CSS class mapping
  const getAspectRatioClass = (ratio) => {
    const ratioMap = {
      '1:1': 'aspect-square',           // Square (logo, instagram)
      '16:9': 'aspect-video',           // Video/Banner (hero, thumbnails)
      '4:3': 'aspect-[4/3]',            // Landscape
      '3:4': 'aspect-[3/4]',            // Portrait (categories)
      '4:5': 'aspect-[4/5]',            // Portrait+ (products, gallery)
      '2:3': 'aspect-[2/3]',            // Tall portrait (fashion trends)
      '3:2': 'aspect-[3/2]',            // Wide landscape
      '2:1': 'aspect-[2/1]',            // Wide banner (promo)
      '3:1': 'aspect-[3/1]',            // Extra wide (sliders, banners)
      '21:9': 'aspect-[21/9]',          // Ultra-wide (hero backgrounds)
    };
    return ratioMap[ratio] || 'aspect-video'; // Default to 16:9
  };

  const FileUploadArea = ({ fieldName, label, accept = "image/*", required = false, dimensions = "", description = "", existingUrl = "", aspectRatio = "16:9" }) => {
    const previewSrc = previewImages[fieldName] || existingUrl;
    const isVideo = accept.includes('video');
    const aspectClass = getAspectRatioClass(aspectRatio);

    // Get aspect ratio display text
    const getAspectDisplay = (ratio) => {
      const displays = {
        '1:1': 'Square',
        '16:9': 'Landscape',
        '4:3': 'Standard',
        '3:4': 'Portrait',
        '4:5': 'Portrait',
        '2:3': 'Tall',
        '3:2': 'Wide',
        '2:1': 'Banner',
        '3:1': 'Wide Banner',
        '21:9': 'Ultra Wide'
      };
      return displays[ratio] || ratio;
    };

    return (
      <div className="space-y-2">
        {/* Label with badges */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <div className="flex items-center gap-2">
            {dimensions && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                {dimensions}
              </span>
            )}
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#D45614]/10 text-[#D45614]">
              {getAspectDisplay(aspectRatio)}
            </span>
          </div>
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}

        <div className="relative">
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileUpload(fieldName, e.target.files[0])}
            className="hidden"
            id={fieldName}
          />

          {previewSrc && !isVideo ? (
            // Clean image preview
            <div className="relative group">
              <div className={`relative ${aspectClass} w-full max-w-xs overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-50`}>
                <img
                  src={previewSrc}
                  alt="Preview"
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <label
                    htmlFor={fieldName}
                    className="px-3 py-1.5 bg-white text-gray-800 rounded cursor-pointer hover:bg-gray-100 transition-colors text-sm font-medium"
                  >
                    Change
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedFiles(prev => {
                        const newFiles = { ...prev };
                        delete newFiles[fieldName];
                        return newFiles;
                      });
                      setPreviewImages(prev => {
                        const newPreviews = { ...prev };
                        delete newPreviews[fieldName];
                        return newPreviews;
                      });
                    }}
                    className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                {/* Uploaded indicator */}
                <div className="absolute top-2 right-2 w-6 h-6 bg-[#D45614] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          ) : (
            // Clean upload area
            <label htmlFor={fieldName} className="block cursor-pointer">
              <div className={`${aspectClass} max-w-xs border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-[#D45614] hover:bg-[#D45614]/5 transition-colors overflow-hidden`}>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    {isVideo ? (
                      <PlayCircle className="w-6 h-6 text-gray-400" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-600 text-center">
                    {uploadedFiles[fieldName] ? uploadedFiles[fieldName].name : 'Click to upload'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {isVideo ? 'MP4, WebM, OGG' : 'PNG, JPG, GIF, SVG'}
                  </p>
                </div>
              </div>
            </label>
          )}
        </div>
      </div>
    );
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your main page configuration has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D45614] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading main page...</p>
          </div>
        </div>
      } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Main Page - Template {templateId}</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-400">
                    Saved {lastSaved.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => console.log('Preview:', formData)}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:text-[#D45614] transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm">Preview</span>
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-1.5 bg-[#D45614] text-white text-sm font-medium hover:bg-[#c04d12] transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* LEFT SIDEBAR - Fixed width */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-24 p-4">
              {/* Progress Summary */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Overall Progress</h4>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${sections.length > 0 ? Math.round((sections.filter(s => formData[s.id] && Object.keys(formData[s.id]).length > 0).length / sections.length) * 100) : 0}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {sections.filter(s => formData[s.id] && Object.keys(formData[s.id]).length > 0).length} of {sections.length} sections
                </p>
              </div>

              {/* Page Sections */}
              <h3 className="text-sm font-semibold text-gray-900 mb-3 px-2">Sections</h3>
              <nav className="space-y-1 max-h-[calc(100vh-350px)] overflow-y-auto pr-1">
                {sections.map((section) => {
                  const SectionIcon = section.icon;
                  const isActive = activeSection === section.id;
                  const hasData = formData[section.id] && Object.keys(formData[section.id]).length > 0;

                  return (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                      }`}
                    >
                      <SectionIcon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="flex-1 text-left truncate text-xs leading-tight">{section.label}</span>
                      {hasData && (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                      {isActive && !hasData && (
                        <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* RIGHT CONTENT - Flexible width */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-8">
              {/* Section Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {sections.find(s => s.id === activeSection)?.label || 'Section'}
                  </h2>
                  {formData[activeSection] && Object.keys(formData[activeSection]).length > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                      <Check className="w-4 h-4" />
                      Completed
                    </span>
                  )}
                </div>
              </div>

              {/* Brand Section */}
              {activeSection === 'brand' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShoppingBag className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Identity</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Define your brand's visual identity and core information that will represent your business across your website.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <FileUploadArea
                        fieldName="brand_logo"
                        label="Brand Logo *"
                        accept="image/*"
                        required={true}
                        dimensions="300x300px"
                        description="Your brand logo will appear across your template"
                        existingUrl={formData.brand?.logo || ''}
                        aspectRatio="1:1"
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Name *
                        </label>
                        <input
                          type="text"
                          value={formData.brand?.name || ''}
                          onChange={(e) => handleInputChange('brand.name', e.target.value)}
                          placeholder="Enter your brand name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tagline
                        </label>
                        <input
                          type="text"
                          value={formData.brand?.tagline || ''}
                          onChange={(e) => handleInputChange('brand.tagline', e.target.value)}
                          placeholder="Your brand tagline"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Description *
                        </label>
                        <textarea
                          value={formData.brand?.description || ''}
                          onChange={(e) => handleInputChange('brand.description', e.target.value)}
                          placeholder="Describe your brand, mission, and what makes you unique..."
                          rows={5}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">This will be used in your website's about section</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedColorPicker
                          label="Primary Color"
                          value={formData.theme?.colors?.primary || '#2C1810'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              primary: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#2C1810', '#4A2F23', '#1A0F0A',
                            '#C9A882', '#E5D4B8', '#A68B6A'
                          ]}
                        />
                        <EnhancedColorPicker
                          label="Secondary Color"
                          value={formData.theme?.colors?.secondary || '#ffffff'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              secondary: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#ffffff', '#f8f9fa', '#e9ecef',
                            '#dee2e6', '#ced4da', '#adb5bd'
                          ]}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedColorPicker
                          label="Accent Color"
                          value={formData.theme?.colors?.accent || '#C9A882'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              accent: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#C9A882', '#E5D4B8', '#A68B6A',
                            '#D4AF37', '#FFD700', '#B8860B'
                          ]}
                        />
                        <EnhancedColorPicker
                          label="Text Color"
                          value={formData.theme?.colors?.text || '#111827'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              text: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#111827', '#1f2937', '#374151',
                            '#4b5563', '#6b7280', '#9ca3af'
                          ]}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedFontSelector
                          label="Heading Font"
                          value={formData.theme?.typography?.heading || formData.theme?.fonts?.heading || 'sans-serif'}
                          onChange={(value) => handleInputChange('theme.typography.heading', value)}
                          category="all"
                        />
                        <EnhancedFontSelector
                          label="Body Font"
                          value={formData.theme?.typography?.body || formData.theme?.fonts?.body || 'sans-serif'}
                          onChange={(value) => handleInputChange('theme.typography.body', value)}
                          category="all"
                        />
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                          <Info className="w-4 h-4 mr-2" />
                          Owner Information (Optional)
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Owner Name
                            </label>
                            <input
                              type="text"
                              value={formData.brand?.owner?.name || ''}
                              onChange={(e) => handleInputChange('brand.owner.name', e.target.value)}
                              placeholder="Your name"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Owner Bio
                            </label>
                            <textarea
                              value={formData.brand?.owner?.bio || ''}
                              onChange={(e) => handleInputChange('brand.owner.bio', e.target.value)}
                              placeholder="Brief bio about the owner/founder"
                              rows={3}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hero Section */}
              {activeSection === 'hero' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Monitor className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Hero Section</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Create an impactful first impression with your hero section that captures visitors' attention immediately.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Title *</label>
                        <input
                          type="text"
                          value={formData.hero?.title || ''}
                          onChange={(e) => handleInputChange('hero.title', e.target.value)}
                          placeholder="e.g., Welcome to Fashion Excellence"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">Main headline that visitors see first</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Subtitle</label>
                        <input
                          type="text"
                          value={formData.hero?.subtitle || ''}
                          onChange={(e) => handleInputChange('hero.subtitle', e.target.value)}
                          placeholder="e.g., Discover timeless elegance"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                        <p className="mt-1 text-xs text-gray-500">Supporting text under the main title</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          value={formData.hero?.description || ''}
                          onChange={(e) => handleInputChange('hero.description', e.target.value)}
                          placeholder="Additional descriptive text about your brand or current offers..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Call-to-Action Text</label>
                          <input
                            type="text"
                            value={formData.hero?.cta_text || 'Shop Collection'}
                            onChange={(e) => handleInputChange('hero.cta_text', e.target.value)}
                            placeholder="Shop Now"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link</label>
                          <input
                            type="text"
                            value={formData.hero?.cta_link || '#products'}
                            onChange={(e) => handleInputChange('hero.cta_link', e.target.value)}
                            placeholder="#products"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <FileUploadArea
                        fieldName="hero_background"
                        label="Hero Background Image *"
                        accept="image/*"
                        required={true}
                        dimensions="1920x800px recommended"
                        description="High-quality background image for your hero section"
                        existingUrl={formData.hero?.background_image || formData.hero?.backgroundImage || ''}
                        aspectRatio="21:9"
                      />

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-3">Hero Value Tags</h4>
                        <p className="text-xs text-gray-500 mb-3">Short labels shown on the hero section (e.g., "Premium Quality", "Handcrafted")</p>
                        <div className="space-y-2">
                          {formData.hero?.values?.map((val, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={val.label || ''}
                                onChange={(e) => {
                                  const newValues = [...(formData.hero?.values || [])];
                                  newValues[index] = { ...newValues[index], label: e.target.value };
                                  handleInputChange('hero.values', newValues);
                                }}
                                placeholder={`Label ${index + 1}`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary"
                              />
                              <button
                                onClick={() => {
                                  const newValues = (formData.hero?.values || []).filter((_, i) => i !== index);
                                  handleInputChange('hero.values', newValues);
                                }}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newValues = [...(formData.hero?.values || []), { label: '' }];
                              handleInputChange('hero.values', newValues);
                            }}
                            className="w-full border border-dashed border-blue-300 rounded-lg p-2 hover:bg-blue-50 transition-all text-sm text-blue-600"
                          >
                            <Plus className="w-4 h-4 inline mr-1" />Add Label
                          </button>
                        </div>
                      </div>

                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                        <h4 className="font-semibold text-purple-900 mb-3">Hero Style Options</h4>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Layout Style</label>
                        <select
                          value={formData.layout?.heroStyle || 'overlay'}
                          onChange={(e) => handleInputChange('layout.heroStyle', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        >
                          <option value="minimal">Minimal - Clean and simple</option>
                          <option value="split">Split - Text and image side by side</option>
                          <option value="overlay">Overlay - Text over background image</option>
                          <option value="fullscreen">Fullscreen - Full viewport height</option>
                          <option value="slider">Slider - Multiple hero slides</option>
                          <option value="video">Video - Video background</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories Section */}
              {activeSection === 'categories' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Layers className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Product Categories</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Create beautiful product categories to organize your inventory and help customers find what they're looking for.
                    </p>
                  </div>

                  {/* Section Header Fields */}
                  <div className="bg-gradient-to-br from-blue-50 to-green-50 border border-blue-200 rounded-2xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Section Header</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                        <input
                          type="text"
                          value={formData.categories?.title || ''}
                          onChange={(e) => handleInputChange('categories.title', e.target.value)}
                          placeholder="e.g., Shop By Category"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                        <input
                          type="text"
                          value={formData.categories?.subtitle || ''}
                          onChange={(e) => handleInputChange('categories.subtitle', e.target.value)}
                          placeholder="e.g., Find your perfect style"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Category Items */}
                  <div className="space-y-6">
                    {formData.categories?.items?.map((category, index) => (
                      <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {category.name || `Category ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeNestedArrayItem('categories', 'items', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete category"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
                              <input
                                type="text"
                                value={category.name || ''}
                                onChange={(e) => updateNestedArrayItem('categories', 'items', index, 'name', e.target.value)}
                                placeholder="e.g., Women's Clothing"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <input
                                type="text"
                                value={category.description || ''}
                                onChange={(e) => updateNestedArrayItem('categories', 'items', index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Item Count</label>
                              <input
                                type="text"
                                value={category.count || ''}
                                onChange={(e) => updateNestedArrayItem('categories', 'items', index, 'count', e.target.value)}
                                placeholder="e.g., 150 products"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
                              <input
                                type="text"
                                value={category.tag || ''}
                                onChange={(e) => updateNestedArrayItem('categories', 'items', index, 'tag', e.target.value)}
                                placeholder="e.g., Popular, New, Trending"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div className="md:col-span-2 lg:col-span-2">
                              <FileUploadArea
                                fieldName={`category_image_${index}`}
                                label="Category Image"
                                accept="image/*"
                                dimensions="400x500px recommended"
                                description="High-quality image representing this category"
                                existingUrl={category.image || ''}
                                aspectRatio="4:5"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addNestedArrayItem('categories', 'items', { name: '', count: '', description: '', tag: '', image: '' })}
                      className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                        Add Category
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Collections Section */}
              {activeSection === 'collections' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Grid className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Collections</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your featured product collections and highlights</p>
                  </div>

                  <div className="space-y-6">
                    {formData.collections?.map((collection, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {collection.title || collection.name || `Collection ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('collections', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete collection"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Collection Name *</label>
                              <input
                                type="text"
                                value={collection.title || collection.name || ''}
                                onChange={(e) => updateArrayItem('collections', index, 'title', e.target.value)}
                                placeholder="e.g., Elite Collection"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <input
                                type="text"
                                value={collection.description || ''}
                                onChange={(e) => updateArrayItem('collections', index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Item Count</label>
                              <input
                                type="text"
                                value={collection.count || ''}
                                onChange={(e) => updateArrayItem('collections', index, 'count', e.target.value)}
                                placeholder="e.g., 24 pieces"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div className="md:col-span-2 lg:col-span-3">
                              <FileUploadArea
                                fieldName={`collection_image_${index}`}
                                label="Collection Image"
                                accept="image/*"
                                dimensions="800x600px recommended"
                                description="Main image for this collection"
                                existingUrl={collection.image || ''}
                                aspectRatio="4:3"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('collections', { title: '', name: '', count: '', description: '', image: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Collection
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Videos Section */}
              {activeSection === 'videos' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video Gallery</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Add engaging video content to showcase your products and brand story.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {formData.videos?.map((video, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {video.title || `Video ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('videos', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete video"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video Title *</label>
                              <input
                                type="text"
                                value={video.title || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'title', e.target.value)}
                                placeholder="e.g., Brand Story"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <textarea
                                value={video.description || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'description', e.target.value)}
                                placeholder="Brief description of the video content..."
                                rows={2}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                              <input
                                type="url"
                                value={video.url || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'url', e.target.value)}
                                placeholder="https://youtube.com/watch?v=..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                              <p className="mt-1 text-xs text-gray-500">Or upload a video file below</p>
                              {/* Video Preview for URL */}
                              {video.url && (
                                <VideoPreview
                                  url={video.url}
                                  onRemove={() => updateArrayItem('videos', index, 'url', '')}
                                />
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video Thumbnail</label>
                              <FileUploadArea
                                fieldName={`video_thumbnail_${index}`}
                                label="Upload Thumbnail"
                                accept="image/*"
                                dimensions="640x360px recommended"
                                description="Thumbnail image for the video"
                                existingUrl={video.thumbnail || ''}
                                aspectRatio="16:9"
                              />
                            </div>

                            <FileUploadArea
                              fieldName={`video_file_${index}`}
                              label="Upload Video File (Optional)"
                              accept="video/*"
                              description="Upload video file directly (MP4, WebM, or OGG)"
                              existingUrl={video.file || ''}
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('videos', { title: '', description: '', url: '', thumbnail: '', file: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Video
                      </span>
                    </button>
                  </div>
                </div>
              )}


              {/* Business Information Section */}
              {activeSection === 'business' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Business Information</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Provide your business contact details and information for customers to reach you.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                        <input
                          type="text"
                          value={formData.business?.contact?.business_name || ''}
                          onChange={(e) => handleInputChange('business.contact.business_name', e.target.value)}
                          placeholder="Official business name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          value={formData.business?.contact?.email || ''}
                          onChange={(e) => handleInputChange('business.contact.email', e.target.value)}
                          placeholder="contact@yourbusiness.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          value={formData.business?.contact?.phone || ''}
                          onChange={(e) => handleInputChange('business.contact.phone', e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Working Hours</label>
                        <input
                          type="text"
                          value={formData.business?.contact?.working_hours || 'Mon-Sat: 10AM-8PM'}
                          onChange={(e) => handleInputChange('business.contact.working_hours', e.target.value)}
                          placeholder="Mon-Fri 9AM-6PM"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
                        <textarea
                          value={formData.business?.contact?.address || ''}
                          onChange={(e) => handleInputChange('business.contact.address', e.target.value)}
                          placeholder="123 Business Street, City, State, ZIP"
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                        <input
                          type="url"
                          value={formData.business?.contact?.website || ''}
                          onChange={(e) => handleInputChange('business.contact.website', e.target.value)}
                          placeholder="https://www.yourbusiness.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {/* Statement Section */}
              {activeSection === 'statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create powerful brand messaging that resonates with your audience.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Main Statement *</label>
                        <textarea
                          value={formData.statement?.title || ''}
                          onChange={(e) => handleInputChange('statement.title', e.target.value)}
                          placeholder="Write your brand's main statement or mission..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Text</label>
                        <textarea
                          value={formData.statement?.text || ''}
                          onChange={(e) => handleInputChange('statement.text', e.target.value)}
                          placeholder="Additional supporting text for your statement..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea
                        fieldName="statement_background_image"
                        label="Statement Background Image"
                        accept="image/*"
                        dimensions="1920x800px"
                        description="Background image for the statement section"
                        existingUrl={formData.statement?.background_image || formData.statement?.backgroundImage || ''}
                        aspectRatio="21:9"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Workshop Section */}
              {activeSection === 'workshop' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Workshop Information</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your workshop, studio, or behind-the-scenes content.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Workshop Title *</label>
                        <input
                          type="text"
                          value={formData.workshop?.title || ''}
                          onChange={(e) => handleInputChange('workshop.title', e.target.value)}
                          placeholder="e.g., Our Artisan Workshop"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                        <input
                          type="text"
                          value={formData.workshop?.location || ''}
                          onChange={(e) => handleInputChange('workshop.location', e.target.value)}
                          placeholder="e.g., Mumbai, India"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Workshop Description</label>
                        <textarea
                          value={formData.workshop?.description || ''}
                          onChange={(e) => handleInputChange('workshop.description', e.target.value)}
                          placeholder="Describe your workshop, processes, and craftsmanship..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea
                        fieldName="workshop_background"
                        label="Workshop Background Image"
                        accept="image/*"
                        dimensions="1920x800px recommended"
                        description="Background image for the workshop section"
                        existingUrl={formData.workshop?.background_image || ''}
                        aspectRatio="21:9"
                      />
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Workshop Values</h4>
                    <div className="space-y-4">
                      {formData.workshop?.values?.map((val, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <button onClick={() => removeNestedArrayItem('workshop', 'values', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                          </div>
                          <input type="text" value={val.title || ''} onChange={(e) => updateNestedArrayItem('workshop', 'values', index, 'title', e.target.value)} placeholder="Value title" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                          <textarea value={val.description || ''} onChange={(e) => updateNestedArrayItem('workshop', 'values', index, 'description', e.target.value)} placeholder="Value description..." rows={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                        </div>
                      ))}
                      <button onClick={() => addNestedArrayItem('workshop', 'values', { title: '', description: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-xl p-4 hover:border-amber-400 hover:bg-amber-50 transition-all group">
                        <Plus className="w-6 h-6 mx-auto text-amber-400 group-hover:text-amber-600 mb-1" />
                        <span className="text-amber-600 text-sm font-medium">Add Value</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Fashion Trends Section */}
              {activeSection === 'fashion_trends' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-pink-600 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Fashion Trends</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the latest fashion trends</p>
                  </div>

                  <div className="space-y-6">
                    {formData.fashion_trends?.map((trend, index) => (
                      <div key={index} className="bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {trend.text || `Trend ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('fashion_trends', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete trend"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Trend Name *</label>
                              <input
                                type="text"
                                value={trend.text || ''}
                                onChange={(e) => updateArrayItem('fashion_trends', index, 'text', e.target.value)}
                                placeholder="e.g., Minimalist Chic"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                              <select
                                value={trend.gender || 'All'}
                                onChange={(e) => updateArrayItem('fashion_trends', index, 'gender', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              >
                                <option value="All">All Genders</option>
                                <option value="Men">Men</option>
                                <option value="Women">Women</option>
                                <option value="Kids">Kids</option>
                              </select>
                            </div>
                          </div>

                          <FileUploadArea
                            fieldName={`fashion_trend_image_${index}`}
                            label="Trend Image"
                            accept="image/*"
                            dimensions="400x600px recommended"
                            description="Image showcasing this fashion trend"
                            existingUrl={trend.image || ''}
                            aspectRatio="2:3"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('fashion_trends', { name: '', text: '', gender: 'All', image: '' })}
                      className="w-full border-2 border-dashed border-pink-300 rounded-2xl p-8 hover:border-pink-400 hover:bg-pink-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-pink-400 group-hover:text-pink-600 mb-3" />
                      <span className="text-pink-600 group-hover:text-pink-700 font-medium">
                        Add Trend
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Gallery Section */}
              {activeSection === 'gallery' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Gallery</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create a beautiful image gallery</p>
                  </div>

                  <div className="space-y-6">
                    {formData.gallery?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {item.title || `Gallery Image ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('gallery', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete image"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Image Title *</label>
                            <input
                              type="text"
                              value={item.title || ''}
                              onChange={(e) => updateArrayItem('gallery', index, 'title', e.target.value)}
                              placeholder="e.g., Portrait Series"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              required
                            />
                          </div>

                          <FileUploadArea
                            fieldName={`gallery_image_${index}`}
                            label="Gallery Image"
                            accept="image/*"
                            dimensions="600x750px recommended"
                            description={`Gallery image ${index + 1}`}
                            existingUrl={item.image || ''}
                            aspectRatio="4:5"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('gallery', { title: '', image: '' })}
                      className="w-full border-2 border-dashed border-teal-300 rounded-2xl p-8 hover:border-teal-400 hover:bg-teal-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-teal-400 group-hover:text-teal-600 mb-3" />
                      <span className="text-teal-600 group-hover:text-teal-700 font-medium">
                        Add Image
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Instagram Section */}
              {activeSection === 'instagram' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Instagram Feed</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your Instagram posts and connect with your audience</p>
                  </div>

                  {/* Instagram Handle & Settings */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Instagram Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Handle</label>
                        <input
                          type="text"
                          value={formData.instagram?.handle || ''}
                          onChange={(e) => handleInputChange('instagram.handle', e.target.value)}
                          placeholder="e.g., @yourbrand"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hashtag</label>
                        <input
                          type="text"
                          value={formData.instagram?.hashtag || ''}
                          onChange={(e) => handleInputChange('instagram.hashtag', e.target.value)}
                          placeholder="e.g., #YourBrandStyle"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                        <input
                          type="text"
                          value={formData.instagram?.title || ''}
                          onChange={(e) => handleInputChange('instagram.title', e.target.value)}
                          placeholder="e.g., Follow Us on Instagram"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Instagram Posts */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900">Instagram Posts</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {formData.instagram?.posts?.map((post, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 border-2 border-dashed border-purple-300 rounded-xl overflow-hidden">
                            {post.image ? (
                              <img src={post.image} alt={`Post ${index + 1}`} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="w-8 h-8 text-purple-300" />
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => removeNestedArrayItem('instagram', 'posts', index)}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                          <div className="mt-2">
                            <FileUploadArea
                              fieldName={`instagram_post_${index}`}
                              label=""
                              accept="image/*"
                              dimensions="1080x1080px"
                              existingUrl={post.image || ''}
                              aspectRatio="1:1"
                              compact={true}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => addNestedArrayItem('instagram', 'posts', { image: '', likes: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-6 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-6 h-6 mx-auto text-purple-400 group-hover:text-purple-600 mb-2" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium text-sm">
                        Add Instagram Post
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* About Section */}
              {activeSection === 'about' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Info className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">About / Vision</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share your brand's vision and story with your audience.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input
                        type="text"
                        value={formData.about?.title || ''}
                        onChange={(e) => handleInputChange('about.title', e.target.value)}
                        placeholder="e.g., Our Vision"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea
                        value={formData.about?.description || ''}
                        onChange={(e) => handleInputChange('about.description', e.target.value)}
                        placeholder="Describe your brand's vision and mission..."
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Lookbook Section */}
              {activeSection === 'lookbook' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Lookbook</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create a visual lookbook to showcase your collections.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.lookbook?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{item.title || `Lookbook ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('lookbook', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input type="text" value={item.title || ''} onChange={(e) => updateArrayItem('lookbook', index, 'title', e.target.value)} placeholder="e.g., Summer Collection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                                <input type="text" value={item.subtitle || ''} onChange={(e) => updateArrayItem('lookbook', index, 'subtitle', e.target.value)} placeholder="e.g., Inspired by nature" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                            </div>
                            <FileUploadArea fieldName={`lookbook_image_${index}`} label="Lookbook Image" accept="image/*" dimensions="800x1000px recommended" description="High-quality lookbook image" existingUrl={item.image || ''} aspectRatio="4:5" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('lookbook', { title: '', subtitle: '', image: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-2xl p-8 hover:border-amber-400 hover:bg-amber-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-amber-400 group-hover:text-amber-600 mb-3" />
                      <span className="text-amber-600 group-hover:text-amber-700 font-medium">Add Lookbook Item</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Featured Products Section */}
              {activeSection === 'featured' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-yellow-600 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Featured Products</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Highlight your best products to feature on the homepage.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.featured?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{item.title || `Product ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('featured', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Product Title *</label>
                                <input type="text" value={item.title || ''} onChange={(e) => updateArrayItem('featured', index, 'title', e.target.value)} placeholder="e.g., Silk Evening Gown" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                                <input type="text" value={item.price || ''} onChange={(e) => updateArrayItem('featured', index, 'price', e.target.value)} placeholder="e.g., ₹2,999" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                            </div>
                            <FileUploadArea fieldName={`featured_image_${index}`} label="Product Image" accept="image/*" dimensions="600x800px recommended" description="Product image" existingUrl={item.image || ''} aspectRatio="3:4" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('featured', { title: '', image: '', price: '' })} className="w-full border-2 border-dashed border-yellow-300 rounded-2xl p-8 hover:border-yellow-400 hover:bg-yellow-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-yellow-400 group-hover:text-yellow-600 mb-3" />
                      <span className="text-yellow-600 group-hover:text-yellow-700 font-medium">Add Featured Product</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Brand Story Section */}
              {activeSection === 'brand_story' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Story</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Tell the story behind your brand with compelling stats.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.brand_story?.title || ''} onChange={(e) => handleInputChange('brand_story.title', e.target.value)} placeholder="e.g., Our Story" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.brand_story?.description || ''} onChange={(e) => handleInputChange('brand_story.description', e.target.value)} placeholder="Tell your brand story..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Stats</h4>
                      <div className="space-y-4">
                        {formData.brand_story?.stats?.map((stat, index) => (
                          <div key={index} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                            <span className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <input type="text" value={stat.value || ''} onChange={(e) => updateNestedArrayItem('brand_story', 'stats', index, 'value', e.target.value)} placeholder="e.g., 10+" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <input type="text" value={stat.label || ''} onChange={(e) => updateNestedArrayItem('brand_story', 'stats', index, 'label', e.target.value)} placeholder="e.g., Years of Experience" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <button onClick={() => removeNestedArrayItem('brand_story', 'stats', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button onClick={() => addNestedArrayItem('brand_story', 'stats', { value: '', label: '' })} className="w-full border-2 border-dashed border-emerald-300 rounded-xl p-4 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                          <Plus className="w-6 h-6 mx-auto text-emerald-400 group-hover:text-emerald-600 mb-1" />
                          <span className="text-emerald-600 text-sm font-medium">Add Stat</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Video Section (single video) */}
              {activeSection === 'video' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add a featured video to showcase your brand.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                        <input type="url" value={formData.video?.url || ''} onChange={(e) => handleInputChange('video.url', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                        <p className="mt-1 text-xs text-gray-500">Paste a YouTube or Vimeo link</p>
                        {formData.video?.url && (
                          <VideoPreview url={formData.video.url} onRemove={() => handleInputChange('video.url', '')} />
                        )}
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="video_thumbnail" label="Video Thumbnail" accept="image/*" dimensions="1280x720px recommended" description="Thumbnail image for the video" existingUrl={formData.video?.thumbnail || ''} aspectRatio="16:9" />
                    </div>
                  </div>
                </div>
              )}

              {/* Luxury Statement Section */}
              {activeSection === 'luxury_statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Luxury Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Define your luxury brand identity with a powerful statement.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.luxury_statement?.title || ''} onChange={(e) => handleInputChange('luxury_statement.title', e.target.value)} placeholder="e.g., Timeless Luxury" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.luxury_statement?.description || ''} onChange={(e) => handleInputChange('luxury_statement.description', e.target.value)} placeholder="Describe your luxury brand philosophy..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                  </div>
                </div>
              )}

              {/* Prestige Services Section */}
              {activeSection === 'prestige_services' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Prestige Services</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Highlight the premium services you offer.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.prestige_services?.map((service, index) => (
                      <div key={index} className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{service.title || `Service ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('prestige_services', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete service">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Service Title *</label>
                            <input type="text" value={service.title || ''} onChange={(e) => updateArrayItem('prestige_services', index, 'title', e.target.value)} placeholder="e.g., Personal Styling" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <textarea value={service.description || ''} onChange={(e) => updateArrayItem('prestige_services', index, 'description', e.target.value)} placeholder="Describe this service..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('prestige_services', { title: '', description: '' })} className="w-full border-2 border-dashed border-indigo-300 rounded-2xl p-8 hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-indigo-400 group-hover:text-indigo-600 mb-3" />
                      <span className="text-indigo-600 group-hover:text-indigo-700 font-medium">Add Service</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Craftsmanship Section */}
              {activeSection === 'craftsmanship' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Craftsmanship</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the art and skill behind your products.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.craftsmanship?.title || ''} onChange={(e) => handleInputChange('craftsmanship.title', e.target.value)} placeholder="e.g., The Art of Craftsmanship" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.craftsmanship?.description || ''} onChange={(e) => handleInputChange('craftsmanship.description', e.target.value)} placeholder="Describe your craftsmanship process..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Stats</h4>
                      <div className="space-y-4">
                        {formData.craftsmanship?.stats?.map((stat, index) => (
                          <div key={index} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                            <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <input type="text" value={stat.value || ''} onChange={(e) => updateNestedArrayItem('craftsmanship', 'stats', index, 'value', e.target.value)} placeholder="e.g., 50+" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <input type="text" value={stat.label || ''} onChange={(e) => updateNestedArrayItem('craftsmanship', 'stats', index, 'label', e.target.value)} placeholder="e.g., Artisans" className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <button onClick={() => removeNestedArrayItem('craftsmanship', 'stats', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button onClick={() => addNestedArrayItem('craftsmanship', 'stats', { value: '', label: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-xl p-4 hover:border-amber-400 hover:bg-amber-50 transition-all group">
                          <Plus className="w-6 h-6 mx-auto text-amber-400 group-hover:text-amber-600 mb-1" />
                          <span className="text-amber-600 text-sm font-medium">Add Stat</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Human Connection Section */}
              {activeSection === 'human_connection' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Human Connection</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share a personal touch that connects with your audience.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.human_connection?.title || ''} onChange={(e) => handleInputChange('human_connection.title', e.target.value)} placeholder="e.g., Made with Love" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quote *</label>
                      <textarea value={formData.human_connection?.quote || ''} onChange={(e) => handleInputChange('human_connection.quote', e.target.value)} placeholder="A meaningful quote that represents your brand..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                      <input type="text" value={formData.human_connection?.author || ''} onChange={(e) => handleInputChange('human_connection.author', e.target.value)} placeholder="e.g., Founder, Brand Name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Brand Statement Section */}
              {activeSection === 'brand_statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Define your brand with a powerful statement and core values.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.brand_statement?.title || ''} onChange={(e) => handleInputChange('brand_statement.title', e.target.value)} placeholder="e.g., What We Stand For" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                      <textarea value={formData.brand_statement?.description || ''} onChange={(e) => handleInputChange('brand_statement.description', e.target.value)} placeholder="Your brand statement..." rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Values</h4>
                      <div className="space-y-4">
                        {formData.brand_statement?.values?.map((val, index) => (
                          <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                              <button onClick={() => removeNestedArrayItem('brand_statement', 'values', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <input type="text" value={val.title || ''} onChange={(e) => updateNestedArrayItem('brand_statement', 'values', index, 'title', e.target.value)} placeholder="Value title" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                            <textarea value={val.description || ''} onChange={(e) => updateNestedArrayItem('brand_statement', 'values', index, 'description', e.target.value)} placeholder="Value description..." rows={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                          </div>
                        ))}
                        <button onClick={() => addNestedArrayItem('brand_statement', 'values', { title: '', description: '' })} className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 hover:border-gray-400 hover:bg-gray-50 transition-all group">
                          <Plus className="w-6 h-6 mx-auto text-gray-400 group-hover:text-gray-600 mb-1" />
                          <span className="text-gray-600 text-sm font-medium">Add Value</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Featured Stories Section */}
              {activeSection === 'featured_stories' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Featured Stories</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share stories that highlight your brand and products.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.featured_stories?.map((story, index) => (
                      <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{story.title || `Story ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('featured_stories', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete story">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                <input type="text" value={story.title || ''} onChange={(e) => updateArrayItem('featured_stories', index, 'title', e.target.value)} placeholder="Story title" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
                                <textarea value={story.excerpt || ''} onChange={(e) => updateArrayItem('featured_stories', index, 'excerpt', e.target.value)} placeholder="Brief excerpt..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                                <input type="text" value={story.category || ''} onChange={(e) => updateArrayItem('featured_stories', index, 'category', e.target.value)} placeholder="e.g., Fashion, Lifestyle" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                              </div>
                            </div>
                            <FileUploadArea fieldName={`featured_story_image_${index}`} label="Story Image" accept="image/*" dimensions="800x600px recommended" description="Story cover image" existingUrl={story.image || ''} aspectRatio="4:3" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('featured_stories', { title: '', excerpt: '', category: '', image: '' })} className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 group-hover:text-blue-700 font-medium">Add Story</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Quote Section */}
              {activeSection === 'quote' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Quote</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add an inspirational quote to your page.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quote Text *</label>
                      <textarea value={formData.quote?.text || ''} onChange={(e) => handleInputChange('quote.text', e.target.value)} placeholder="Enter the quote text..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Author</label>
                      <input type="text" value={formData.quote?.author || ''} onChange={(e) => handleInputChange('quote.author', e.target.value)} placeholder="e.g., Jane Doe, Founder" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Portfolio Section */}
              {activeSection === 'portfolio' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Portfolio</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your best work in a portfolio gallery.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.portfolio?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                            <h4 className="text-lg font-semibold text-gray-900">{item.title || `Portfolio ${index + 1}`}</h4>
                          </div>
                          <button onClick={() => removeArrayItem('portfolio', index)} className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors" title="Delete item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                              <input type="text" value={item.title || ''} onChange={(e) => updateArrayItem('portfolio', index, 'title', e.target.value)} placeholder="e.g., Evening Collection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                            </div>
                            <FileUploadArea fieldName={`portfolio_image_${index}`} label="Portfolio Image" accept="image/*" dimensions="800x600px recommended" description="Portfolio piece image" existingUrl={item.image || ''} aspectRatio="4:3" />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('portfolio', { title: '', image: '' })} className="w-full border-2 border-dashed border-teal-300 rounded-2xl p-8 hover:border-teal-400 hover:bg-teal-50 transition-all duration-300 group">
                      <Plus className="w-8 h-8 mx-auto text-teal-400 group-hover:text-teal-600 mb-3" />
                      <span className="text-teal-600 group-hover:text-teal-700 font-medium">Add Portfolio Item</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Artist Statement Section */}
              {activeSection === 'artist_statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Artist Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Express your artistic vision and philosophy.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.artist_statement?.title || ''} onChange={(e) => handleInputChange('artist_statement.title', e.target.value)} placeholder="e.g., My Artistic Vision" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Statement *</label>
                      <textarea value={formData.artist_statement?.text || ''} onChange={(e) => handleInputChange('artist_statement.text', e.target.value)} placeholder="Share your artistic statement and philosophy..." rows={6} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                  </div>
                </div>
              )}

              {/* Announcement Section */}
              {activeSection === 'announcement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Info className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Announcement Bar</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create an eye-catching announcement bar for promotions, sales, or important updates.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Announcement Text *</label>
                      <input type="text" value={formData.announcement?.text || ''} onChange={(e) => handleInputChange('announcement.text', e.target.value)} placeholder="e.g., Free shipping on orders over ₹999!" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      <p className="mt-1 text-xs text-gray-500">Keep it short and impactful</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Link Text (Optional)</label>
                      <input type="text" value={formData.announcement?.linkText || ''} onChange={(e) => handleInputChange('announcement.linkText', e.target.value)} placeholder="e.g., Shop Now →" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Promo Section */}
              {activeSection === 'promo' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-pink-600 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Promo Banner</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create promotional offers with discount codes.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Promo Message *</label>
                      <input type="text" value={formData.promo?.message || ''} onChange={(e) => handleInputChange('promo.message', e.target.value)} placeholder="e.g., Extra 20% Off Everything!" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Promo Code</label>
                      <input type="text" value={formData.promo?.code || ''} onChange={(e) => handleInputChange('promo.code', e.target.value)} placeholder="e.g., SAVE20" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary font-mono" />
                    </div>
                  </div>
                </div>
              )}

              {/* Marquee Section */}
              {activeSection === 'marquee' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Type className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Marquee Banner</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create an animated scrolling text banner.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Marquee Text *</label>
                    <input type="text" value={formData.marquee?.text || ''} onChange={(e) => handleInputChange('marquee.text', e.target.value)} placeholder="e.g., NEW ARRIVALS • FREE SHIPPING • 50% OFF SALE •" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    <p className="mt-1 text-xs text-gray-500">Use • to separate items for a continuous scroll effect</p>
                  </div>
                </div>
              )}

              {/* Products Section */}
              {activeSection === 'products' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShoppingBag className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Products Section</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Configure how your products section appears on the homepage.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Label</label>
                      <input type="text" value={formData.products?.label || ''} onChange={(e) => handleInputChange('products.label', e.target.value)} placeholder="e.g., Featured" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Title *</label>
                      <input type="text" value={formData.products?.title || formData.products?.section_title || ''} onChange={(e) => handleInputChange('products.title', e.target.value)} placeholder="e.g., New Arrivals" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                      <input type="text" value={formData.products?.section_subtitle || ''} onChange={(e) => handleInputChange('products.section_subtitle', e.target.value)} placeholder="e.g., Discover our latest collection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                      <input type="text" value={formData.products?.ctaText || ''} onChange={(e) => handleInputChange('products.ctaText', e.target.value)} placeholder="e.g., View All" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Intro Section */}
              {activeSection === 'intro' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-stone-600 to-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Introduction</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create a compelling introduction with a featured quote.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quote *</label>
                      <textarea value={formData.intro?.quote || ''} onChange={(e) => handleInputChange('intro.quote', e.target.value)} placeholder="An inspiring quote that represents your brand..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea value={formData.intro?.description || ''} onChange={(e) => handleInputChange('intro.description', e.target.value)} placeholder="Additional context or description..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Attribution</label>
                      <input type="text" value={formData.intro?.attribution || ''} onChange={(e) => handleInputChange('intro.attribution', e.target.value)} placeholder="e.g., — Since 2015" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Testimonials Section */}
              {activeSection === 'testimonials' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Customer Testimonials</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase reviews and testimonials from happy customers.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Label</label>
                      <input type="text" value={formData.testimonials?.label || ''} onChange={(e) => handleInputChange('testimonials.label', e.target.value)} placeholder="e.g., What Our Customers Say" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                      <input type="text" value={formData.testimonials?.title || ''} onChange={(e) => handleInputChange('testimonials.title', e.target.value)} placeholder="e.g., Customer Reviews" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="space-y-6">
                    {formData.testimonials?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-sm p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('testimonials', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="space-y-4">
                          <textarea value={item.text || ''} onChange={(e) => updateNestedArrayItem('testimonials', 'items', index, 'text', e.target.value)} placeholder="Customer testimonial text..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <input type="text" value={item.name || ''} onChange={(e) => updateNestedArrayItem('testimonials', 'items', index, 'name', e.target.value)} placeholder="Customer Name" className="px-4 py-3 border border-gray-300 rounded-lg" />
                            <input type="text" value={item.location || item.city || ''} onChange={(e) => updateNestedArrayItem('testimonials', 'items', index, 'location', e.target.value)} placeholder="Location/City" className="px-4 py-3 border border-gray-300 rounded-lg" />
                            <input type="text" value={item.initial || ''} onChange={(e) => updateNestedArrayItem('testimonials', 'items', index, 'initial', e.target.value)} placeholder="Initial (A)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                            <select value={item.rating || 5} onChange={(e) => updateNestedArrayItem('testimonials', 'items', index, 'rating', parseInt(e.target.value))} className="px-4 py-3 border border-gray-300 rounded-lg">
                              <option value={5}>5 Stars</option>
                              <option value={4}>4 Stars</option>
                              <option value={3}>3 Stars</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('testimonials', 'items', { text: '', name: '', location: '', initial: '', rating: 5 })} className="w-full border-2 border-dashed border-green-300 rounded-2xl p-8 hover:border-green-400 hover:bg-green-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-green-400 group-hover:text-green-600 mb-3" />
                      <span className="text-green-600 font-medium">Add Testimonial</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Newsletter Section */}
              {activeSection === 'newsletter' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Newsletter Signup</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Configure your newsletter signup section to grow your email list.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.newsletter?.title || ''} onChange={(e) => handleInputChange('newsletter.title', e.target.value)} placeholder="e.g., Join Our Newsletter" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                      <input type="text" value={formData.newsletter?.subtitle || formData.newsletter?.description || ''} onChange={(e) => handleInputChange('newsletter.subtitle', e.target.value)} placeholder="e.g., Get 10% off your first order" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder Text</label>
                      <input type="text" value={formData.newsletter?.placeholder || ''} onChange={(e) => handleInputChange('newsletter.placeholder', e.target.value)} placeholder="e.g., Enter your email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                      <input type="text" value={formData.newsletter?.buttonText || formData.newsletter?.button_text || ''} onChange={(e) => handleInputChange('newsletter.buttonText', e.target.value)} placeholder="e.g., Subscribe" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Disclaimer</label>
                      <input type="text" value={formData.newsletter?.disclaimer || ''} onChange={(e) => handleInputChange('newsletter.disclaimer', e.target.value)} placeholder="e.g., We respect your privacy. Unsubscribe anytime." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Brand Statement (brandStatement) */}
              {activeSection === 'brandStatement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-zinc-700 to-zinc-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Define your brand with a powerful statement.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quote *</label>
                      <textarea value={formData.brandStatement?.quote || ''} onChange={(e) => handleInputChange('brandStatement.quote', e.target.value)} placeholder="Your brand's defining statement..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea value={formData.brandStatement?.description || ''} onChange={(e) => handleInputChange('brandStatement.description', e.target.value)} placeholder="Additional context..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Philosophy Section */}
              {activeSection === 'philosophy' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-stone-600 to-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Philosophy</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share your brand's core philosophy and values.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.philosophy?.label || ''} onChange={(e) => handleInputChange('philosophy.label', e.target.value)} placeholder="e.g., Our Philosophy" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.philosophy?.title || ''} onChange={(e) => handleInputChange('philosophy.title', e.target.value)} placeholder="e.g., Crafted with Purpose" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                        <textarea value={formData.philosophy?.description || formData.philosophy?.description1 || ''} onChange={(e) => handleInputChange('philosophy.description', e.target.value)} placeholder="Describe your brand philosophy..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                        <input type="text" value={formData.philosophy?.ctaText || ''} onChange={(e) => handleInputChange('philosophy.ctaText', e.target.value)} placeholder="e.g., Learn More" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="philosophy_image" label="Philosophy Image" accept="image/*" dimensions="800x1000px recommended" description="Image for the philosophy section" existingUrl={formData.philosophy?.image || ''} aspectRatio="4:5" />
                    </div>
                  </div>
                </div>
              )}

              {/* Editorial Section */}
              {activeSection === 'editorial' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-stone-600 to-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Editorial</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create editorial content to showcase your brand story.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.editorial?.label || ''} onChange={(e) => handleInputChange('editorial.label', e.target.value)} placeholder="e.g., Editorial" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.editorial?.title || ''} onChange={(e) => handleInputChange('editorial.title', e.target.value)} placeholder="e.g., The Latest Edit" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                        <input type="text" value={formData.editorial?.subtitle || ''} onChange={(e) => handleInputChange('editorial.subtitle', e.target.value)} placeholder="e.g., Seasonal Trends" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                        <input type="text" value={formData.editorial?.ctaText || ''} onChange={(e) => handleInputChange('editorial.ctaText', e.target.value)} placeholder="e.g., Explore Now" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="editorial_image" label="Editorial Image" accept="image/*" dimensions="1200x800px recommended" description="Main editorial image" existingUrl={formData.editorial?.image || formData.editorial?.backgroundImage || ''} aspectRatio="3:2" />
                    </div>
                  </div>
                </div>
              )}

              {/* Features Section */}
              {activeSection === 'features' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Features</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Highlight key features and benefits of your brand or products.</p>
                  </div>
                  <div className="space-y-6">
                    {(Array.isArray(formData.features) ? formData.features : formData.features?.items || []).map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => Array.isArray(formData.features) ? removeArrayItem('features', index) : removeNestedArrayItem('features', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <input type="text" value={item.icon || ''} onChange={(e) => Array.isArray(formData.features) ? updateArrayItem('features', index, 'icon', e.target.value) : updateNestedArrayItem('features', 'items', index, 'icon', e.target.value)} placeholder="Icon (e.g., ZapIcon)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.title || ''} onChange={(e) => Array.isArray(formData.features) ? updateArrayItem('features', index, 'title', e.target.value) : updateNestedArrayItem('features', 'items', index, 'title', e.target.value)} placeholder="Feature Title" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.description || ''} onChange={(e) => Array.isArray(formData.features) ? updateArrayItem('features', index, 'description', e.target.value) : updateNestedArrayItem('features', 'items', index, 'description', e.target.value)} placeholder="Feature Description" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => Array.isArray(formData.features) ? addArrayItem('features', { icon: '', title: '', description: '' }) : addNestedArrayItem('features', 'items', { icon: '', title: '', description: '' })} className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 font-medium">Add Feature</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Services Section */}
              {activeSection === 'services' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Services</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">List the services you offer to customers.</p>
                  </div>
                  <div className="space-y-6">
                    {(Array.isArray(formData.services) ? formData.services : formData.services?.items || []).map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => Array.isArray(formData.services) ? removeArrayItem('services', index) : removeNestedArrayItem('services', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <input type="text" value={item.icon || ''} onChange={(e) => Array.isArray(formData.services) ? updateArrayItem('services', index, 'icon', e.target.value) : updateNestedArrayItem('services', 'items', index, 'icon', e.target.value)} placeholder="Icon (e.g., Truck)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.title || ''} onChange={(e) => Array.isArray(formData.services) ? updateArrayItem('services', index, 'title', e.target.value) : updateNestedArrayItem('services', 'items', index, 'title', e.target.value)} placeholder="Service Title" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.description || item.subtitle || ''} onChange={(e) => Array.isArray(formData.services) ? updateArrayItem('services', index, 'description', e.target.value) : updateNestedArrayItem('services', 'items', index, 'description', e.target.value)} placeholder="Service Description" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => Array.isArray(formData.services) ? addArrayItem('services', { icon: '', title: '', description: '' }) : addNestedArrayItem('services', 'items', { icon: '', title: '', description: '' })} className="w-full border-2 border-dashed border-gray-300 rounded-2xl p-8 hover:border-gray-400 hover:bg-gray-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-gray-400 group-hover:text-gray-600 mb-3" />
                      <span className="text-gray-600 font-medium">Add Service</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Services Bar Section */}
              {activeSection === 'servicesBar' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-slate-600 to-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Services Bar</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add quick service highlights like free shipping, returns, etc.</p>
                  </div>
                  <div className="space-y-6">
                    {formData.servicesBar?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-slate-700 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeArrayItem('servicesBar', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <input type="text" value={item.icon || ''} onChange={(e) => updateArrayItem('servicesBar', index, 'icon', e.target.value)} placeholder="Icon (e.g., TruckIcon)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.title || ''} onChange={(e) => updateArrayItem('servicesBar', index, 'title', e.target.value)} placeholder="Title (e.g., Free Shipping)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.description || ''} onChange={(e) => updateArrayItem('servicesBar', index, 'description', e.target.value)} placeholder="Description" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('servicesBar', { icon: '', title: '', description: '' })} className="w-full border-2 border-dashed border-slate-300 rounded-2xl p-8 hover:border-slate-400 hover:bg-slate-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-slate-400 group-hover:text-slate-600 mb-3" />
                      <span className="text-slate-600 font-medium">Add Service</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Athlete Section */}
              {activeSection === 'athlete' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Athlete Showcase</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Feature an athlete or brand ambassador.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.athlete?.label || ''} onChange={(e) => handleInputChange('athlete.label', e.target.value)} placeholder="e.g., Featured Athlete" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quote *</label>
                        <textarea value={formData.athlete?.quote || ''} onChange={(e) => handleInputChange('athlete.quote', e.target.value)} placeholder="Athlete quote or statement..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea value={formData.athlete?.description || ''} onChange={(e) => handleInputChange('athlete.description', e.target.value)} placeholder="About the athlete..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                        <input type="text" value={formData.athlete?.ctaText || ''} onChange={(e) => handleInputChange('athlete.ctaText', e.target.value)} placeholder="e.g., Shop the Collection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="athlete_image" label="Athlete Background Image" accept="image/*" dimensions="1920x1080px recommended" description="Full-width background image" existingUrl={formData.athlete?.backgroundImage || ''} aspectRatio="16:9" />
                    </div>
                  </div>
                </div>
              )}

              {/* Brand Values Section */}
              {activeSection === 'brandValues' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Values</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your core brand values.</p>
                  </div>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.brandValues?.label || ''} onChange={(e) => handleInputChange('brandValues.label', e.target.value)} placeholder="e.g., Our Values" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.brandValues?.title || ''} onChange={(e) => handleInputChange('brandValues.title', e.target.value)} placeholder="e.g., Built on Passion" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea value={formData.brandValues?.description || ''} onChange={(e) => handleInputChange('brandValues.description', e.target.value)} placeholder="Describe your brand values..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                      <input type="text" value={formData.brandValues?.ctaText || ''} onChange={(e) => handleInputChange('brandValues.ctaText', e.target.value)} placeholder="e.g., Learn More" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Flash Sale Section */}
              {activeSection === 'flashSale' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Flash Sale</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create urgency with a limited-time flash sale banner.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.flashSale?.title || ''} onChange={(e) => handleInputChange('flashSale.title', e.target.value)} placeholder="e.g., Flash Sale!" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                      <input type="text" value={formData.flashSale?.subtitle || ''} onChange={(e) => handleInputChange('flashSale.subtitle', e.target.value)} placeholder="e.g., Up to 70% Off" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Time (ISO format)</label>
                      <input type="datetime-local" value={formData.flashSale?.endTime || ''} onChange={(e) => handleInputChange('flashSale.endTime', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                      <input type="text" value={formData.flashSale?.ctaText || ''} onChange={(e) => handleInputChange('flashSale.ctaText', e.target.value)} placeholder="e.g., Shop Now" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Video Banner Section */}
              {activeSection === 'videoBanner' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-neutral-700 to-neutral-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video Banner</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add a video banner to showcase your brand.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.videoBanner?.label || ''} onChange={(e) => handleInputChange('videoBanner.label', e.target.value)} placeholder="e.g., Watch" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.videoBanner?.title || ''} onChange={(e) => handleInputChange('videoBanner.title', e.target.value)} placeholder="e.g., Behind the Scenes" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                        <input type="url" value={formData.videoBanner?.videoUrl || ''} onChange={(e) => handleInputChange('videoBanner.videoUrl', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                        {formData.videoBanner?.videoUrl && <VideoPreview url={formData.videoBanner.videoUrl} />}
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="video_banner_image" label="Video Thumbnail" accept="image/*" dimensions="1280x720px recommended" description="Thumbnail image for the video" existingUrl={formData.videoBanner?.image || ''} aspectRatio="16:9" />
                    </div>
                  </div>
                </div>
              )}

              {/* Video Campaign Section */}
              {activeSection === 'videoCampaign' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-zinc-700 to-zinc-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video Campaign</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Feature a video campaign or promotional video.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.videoCampaign?.label || ''} onChange={(e) => handleInputChange('videoCampaign.label', e.target.value)} placeholder="e.g., Campaign" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.videoCampaign?.title || ''} onChange={(e) => handleInputChange('videoCampaign.title', e.target.value)} placeholder="e.g., Spring Collection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                        <input type="url" value={formData.videoCampaign?.videoUrl || ''} onChange={(e) => handleInputChange('videoCampaign.videoUrl', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                        {formData.videoCampaign?.videoUrl && <VideoPreview url={formData.videoCampaign.videoUrl} />}
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="video_campaign_image" label="Campaign Thumbnail" accept="image/*" dimensions="1280x720px recommended" description="Thumbnail for the campaign video" existingUrl={formData.videoCampaign?.image || ''} aspectRatio="16:9" />
                    </div>
                  </div>
                </div>
              )}

              {/* Video Lookbook Section */}
              {activeSection === 'videoLookbook' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video Lookbook</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your collection with a video lookbook.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.videoLookbook?.label || ''} onChange={(e) => handleInputChange('videoLookbook.label', e.target.value)} placeholder="e.g., Lookbook" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.videoLookbook?.title || ''} onChange={(e) => handleInputChange('videoLookbook.title', e.target.value)} placeholder="e.g., Fall/Winter 2024" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                        <input type="url" value={formData.videoLookbook?.videoUrl || ''} onChange={(e) => handleInputChange('videoLookbook.videoUrl', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                        {formData.videoLookbook?.videoUrl && <VideoPreview url={formData.videoLookbook.videoUrl} />}
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="video_lookbook_image" label="Lookbook Thumbnail" accept="image/*" dimensions="1280x720px recommended" description="Thumbnail for the video lookbook" existingUrl={formData.videoLookbook?.image || ''} aspectRatio="16:9" />
                    </div>
                  </div>
                </div>
              )}

              {/* Reviews Section */}
              {activeSection === 'reviews' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Customer Reviews</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Display customer reviews with ratings.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Label</label>
                      <input type="text" value={formData.reviews?.label || ''} onChange={(e) => handleInputChange('reviews.label', e.target.value)} placeholder="e.g., Reviews" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                      <input type="text" value={formData.reviews?.title || ''} onChange={(e) => handleInputChange('reviews.title', e.target.value)} placeholder="e.g., What Our Customers Say" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="space-y-6">
                    {formData.reviews?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('reviews', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="space-y-4">
                          <textarea value={item.text || ''} onChange={(e) => updateNestedArrayItem('reviews', 'items', index, 'text', e.target.value)} placeholder="Customer review text..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <input type="text" value={item.name || ''} onChange={(e) => updateNestedArrayItem('reviews', 'items', index, 'name', e.target.value)} placeholder="Name" className="px-4 py-3 border border-gray-300 rounded-lg" />
                            <input type="text" value={item.city || ''} onChange={(e) => updateNestedArrayItem('reviews', 'items', index, 'city', e.target.value)} placeholder="City" className="px-4 py-3 border border-gray-300 rounded-lg" />
                            <input type="text" value={item.initial || ''} onChange={(e) => updateNestedArrayItem('reviews', 'items', index, 'initial', e.target.value)} placeholder="Initial (A)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                            <select value={item.rating || 5} onChange={(e) => updateNestedArrayItem('reviews', 'items', index, 'rating', parseInt(e.target.value))} className="px-4 py-3 border border-gray-300 rounded-lg">
                              <option value={5}>5 Stars</option>
                              <option value={4}>4 Stars</option>
                              <option value={3}>3 Stars</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('reviews', 'items', { text: '', name: '', city: '', initial: '', rating: 5 })} className="w-full border-2 border-dashed border-yellow-300 rounded-2xl p-8 hover:border-yellow-400 hover:bg-yellow-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-yellow-400 group-hover:text-yellow-600 mb-3" />
                      <span className="text-yellow-600 font-medium">Add Review</span>
                    </button>
                  </div>
                </div>
              )}

              {/* The Edit Section */}
              {activeSection === 'theEdit' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-zinc-700 to-zinc-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Edit className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">The Edit</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create a curated editorial section.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.theEdit?.label || ''} onChange={(e) => handleInputChange('theEdit.label', e.target.value)} placeholder="e.g., The Edit" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.theEdit?.title || ''} onChange={(e) => handleInputChange('theEdit.title', e.target.value)} placeholder="e.g., Curated Selection" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea value={formData.theEdit?.description || ''} onChange={(e) => handleInputChange('theEdit.description', e.target.value)} placeholder="Description of the edit..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                        <input type="text" value={formData.theEdit?.ctaText || ''} onChange={(e) => handleInputChange('theEdit.ctaText', e.target.value)} placeholder="e.g., Explore" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="the_edit_image" label="Edit Image" accept="image/*" dimensions="800x1000px recommended" description="Featured image for The Edit" existingUrl={formData.theEdit?.image || ''} aspectRatio="4:5" />
                    </div>
                  </div>
                </div>
              )}

              {/* Featured Brands Section */}
              {activeSection === 'featuredBrands' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-slate-600 to-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Layers className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Featured Brands</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the brands you carry in your store.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Label</label>
                      <input type="text" value={formData.featuredBrands?.label || ''} onChange={(e) => handleInputChange('featuredBrands.label', e.target.value)} placeholder="e.g., Our Brands" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                      <input type="text" value={formData.featuredBrands?.title || ''} onChange={(e) => handleInputChange('featuredBrands.title', e.target.value)} placeholder="e.g., Shop by Brand" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="space-y-6">
                    {formData.featuredBrands?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-slate-700 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('featuredBrands', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <input type="text" value={item.name || ''} onChange={(e) => updateNestedArrayItem('featuredBrands', 'items', index, 'name', e.target.value)} placeholder="Brand Name" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.products || ''} onChange={(e) => updateNestedArrayItem('featuredBrands', 'items', index, 'products', e.target.value)} placeholder="e.g., 150+ Products" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <FileUploadArea fieldName={`featured_brand_${index}`} label="Brand Logo" accept="image/*" dimensions="200x100px" existingUrl={item.image || ''} aspectRatio="2:1" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('featuredBrands', 'items', { name: '', products: '', image: '' })} className="w-full border-2 border-dashed border-slate-300 rounded-2xl p-8 hover:border-slate-400 hover:bg-slate-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-slate-400 group-hover:text-slate-600 mb-3" />
                      <span className="text-slate-600 font-medium">Add Brand</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Trust Banner Section */}
              {activeSection === 'trustBanner' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-slate-600 to-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Trust Banner</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Build trust with a reassuring banner.</p>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input type="text" value={formData.trustBanner?.title || ''} onChange={(e) => handleInputChange('trustBanner.title', e.target.value)} placeholder="e.g., Shop with Confidence" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea value={formData.trustBanner?.description || ''} onChange={(e) => handleInputChange('trustBanner.description', e.target.value)} placeholder="Reassuring message to customers..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Consultation Section */}
              {activeSection === 'consultation' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Consultation</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Offer personalized consultation services.</p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.consultation?.label || ''} onChange={(e) => handleInputChange('consultation.label', e.target.value)} placeholder="e.g., Personal Service" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.consultation?.title || ''} onChange={(e) => handleInputChange('consultation.title', e.target.value)} placeholder="e.g., Book a Consultation" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea value={formData.consultation?.description || ''} onChange={(e) => handleInputChange('consultation.description', e.target.value)} placeholder="Describe your consultation service..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                        <input type="text" value={formData.consultation?.ctaText || ''} onChange={(e) => handleInputChange('consultation.ctaText', e.target.value)} placeholder="e.g., Book Now" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea fieldName="consultation_image" label="Consultation Image" accept="image/*" dimensions="800x1000px recommended" description="Image for the consultation section" existingUrl={formData.consultation?.image || ''} aspectRatio="4:5" />
                    </div>
                  </div>
                </div>
              )}

              {/* Stores Section */}
              {activeSection === 'stores' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Grid className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Store Locations</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">List your physical store locations.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Label</label>
                      <input type="text" value={formData.stores?.label || ''} onChange={(e) => handleInputChange('stores.label', e.target.value)} placeholder="e.g., Visit Us" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                      <input type="text" value={formData.stores?.title || ''} onChange={(e) => handleInputChange('stores.title', e.target.value)} placeholder="e.g., Our Stores" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="space-y-6">
                    {formData.stores?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('stores', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <input type="text" value={item.city || ''} onChange={(e) => updateNestedArrayItem('stores', 'items', index, 'city', e.target.value)} placeholder="City" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.address || ''} onChange={(e) => updateNestedArrayItem('stores', 'items', index, 'address', e.target.value)} placeholder="Address" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.phone || ''} onChange={(e) => updateNestedArrayItem('stores', 'items', index, 'phone', e.target.value)} placeholder="Phone" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('stores', 'items', { city: '', address: '', phone: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-2xl p-8 hover:border-amber-400 hover:bg-amber-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-amber-400 group-hover:text-amber-600 mb-3" />
                      <span className="text-amber-600 font-medium">Add Store</span>
                    </button>
                  </div>
                </div>
              )}

              {/* City Collections Section */}
              {activeSection === 'cityCollections' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-fuchsia-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Grid className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">City Collections</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase collections inspired by different cities.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Label</label>
                      <input type="text" value={formData.cityCollections?.label || ''} onChange={(e) => handleInputChange('cityCollections.label', e.target.value)} placeholder="e.g., Inspired By" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                      <input type="text" value={formData.cityCollections?.title || ''} onChange={(e) => handleInputChange('cityCollections.title', e.target.value)} placeholder="e.g., City Vibes" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="space-y-6">
                    {formData.cityCollections?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-fuchsia-50 to-purple-50 border border-fuchsia-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-fuchsia-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('cityCollections', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <input type="text" value={item.city || ''} onChange={(e) => updateNestedArrayItem('cityCollections', 'items', index, 'city', e.target.value)} placeholder="City Name" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.vibe || ''} onChange={(e) => updateNestedArrayItem('cityCollections', 'items', index, 'vibe', e.target.value)} placeholder="Vibe (e.g., Urban Chic)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                        <FileUploadArea fieldName={`city_collection_${index}`} label="City Image" accept="image/*" dimensions="600x800px recommended" existingUrl={item.image || ''} aspectRatio="3:4" />
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('cityCollections', 'items', { city: '', vibe: '', image: '' })} className="w-full border-2 border-dashed border-fuchsia-300 rounded-2xl p-8 hover:border-fuchsia-400 hover:bg-fuchsia-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-fuchsia-400 group-hover:text-fuchsia-600 mb-3" />
                      <span className="text-fuchsia-600 font-medium">Add City Collection</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Behind the Scenes Section */}
              {activeSection === 'behindScenes' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-stone-600 to-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Behind the Scenes</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Give customers a peek behind the curtain.</p>
                  </div>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                        <input type="text" value={formData.behindScenes?.label || ''} onChange={(e) => handleInputChange('behindScenes.label', e.target.value)} placeholder="e.g., Behind the Scenes" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                        <input type="text" value={formData.behindScenes?.title || ''} onChange={(e) => handleInputChange('behindScenes.title', e.target.value)} placeholder="e.g., Our Process" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" required />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea value={formData.behindScenes?.description || ''} onChange={(e) => handleInputChange('behindScenes.description', e.target.value)} placeholder="Describe your behind-the-scenes process..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                      <input type="text" value={formData.behindScenes?.ctaText || ''} onChange={(e) => handleInputChange('behindScenes.ctaText', e.target.value)} placeholder="e.g., Learn More" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="space-y-6">
                    <h4 className="text-lg font-semibold text-gray-900">Process Steps</h4>
                    {formData.behindScenes?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-stone-700 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('behindScenes', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input type="text" value={item.title || ''} onChange={(e) => updateNestedArrayItem('behindScenes', 'items', index, 'title', e.target.value)} placeholder="Step Title" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <FileUploadArea fieldName={`behind_scenes_${index}`} label="Step Image" accept="image/*" dimensions="600x400px recommended" existingUrl={item.image || ''} aspectRatio="3:2" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('behindScenes', 'items', { title: '', image: '' })} className="w-full border-2 border-dashed border-stone-300 rounded-2xl p-8 hover:border-stone-400 hover:bg-stone-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-stone-400 group-hover:text-stone-600 mb-3" />
                      <span className="text-stone-600 font-medium">Add Step</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Designers Section */}
              {activeSection === 'designers' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Designers</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the designers behind your collections.</p>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.designers?.title || ''} onChange={(e) => handleInputChange('designers.title', e.target.value)} placeholder="e.g., Meet Our Designers" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-6">
                    {formData.designers?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('designers', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <input type="text" value={item.name || ''} onChange={(e) => updateNestedArrayItem('designers', 'items', index, 'name', e.target.value)} placeholder="Designer Name" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.specialty || ''} onChange={(e) => updateNestedArrayItem('designers', 'items', index, 'specialty', e.target.value)} placeholder="Specialty (e.g., Evening Wear)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                        <FileUploadArea fieldName={`designer_${index}`} label="Designer Photo" accept="image/*" dimensions="400x400px recommended" existingUrl={item.image || ''} aspectRatio="1:1" />
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('designers', 'items', { name: '', specialty: '', image: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-2xl p-8 hover:border-amber-400 hover:bg-amber-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-amber-400 group-hover:text-amber-600 mb-3" />
                      <span className="text-amber-600 font-medium">Add Designer</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Brand Story Section (camelCase version) */}
              {activeSection === 'brandStory' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-stone-600 to-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Story</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share your brand's heritage, values, and journey with your customers.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Label</label>
                      <input type="text" value={formData.brandStory?.label || ''} onChange={(e) => handleInputChange('brandStory.label', e.target.value)} placeholder="e.g., Our Story" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                      <input type="text" value={formData.brandStory?.title || ''} onChange={(e) => handleInputChange('brandStory.title', e.target.value)} placeholder="e.g., Crafting Elegance Since 1990" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Main Description</label>
                    <textarea value={formData.brandStory?.description || ''} onChange={(e) => handleInputChange('brandStory.description', e.target.value)} placeholder="Tell your brand's main story..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Text</label>
                    <textarea value={formData.brandStory?.secondaryText || ''} onChange={(e) => handleInputChange('brandStory.secondaryText', e.target.value)} placeholder="Additional story details..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <FileUploadArea fieldName="brandStory_image" label="Brand Story Image" accept="image/*" dimensions="800x600px recommended" existingUrl={formData.brandStory?.image || ''} aspectRatio="4:3" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text</label>
                      <input type="text" value={formData.brandStory?.ctaText || ''} onChange={(e) => handleInputChange('brandStory.ctaText', e.target.value)} placeholder="e.g., Learn More" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link</label>
                      <input type="text" value={formData.brandStory?.ctaLink || ''} onChange={(e) => handleInputChange('brandStory.ctaLink', e.target.value)} placeholder="e.g., /about" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">Brand Stats</label>
                    <div className="space-y-4">
                      {formData.brandStory?.stats?.map((stat, index) => (
                        <div key={index} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                          <span className="w-8 h-8 bg-stone-600 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <input type="text" value={stat.value || ''} onChange={(e) => updateNestedArrayItem('brandStory', 'stats', index, 'value', e.target.value)} placeholder="e.g., 30+" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg" />
                          <input type="text" value={stat.label || ''} onChange={(e) => updateNestedArrayItem('brandStory', 'stats', index, 'label', e.target.value)} placeholder="e.g., Years of Excellence" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg" />
                          <button onClick={() => removeNestedArrayItem('brandStory', 'stats', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                      <button onClick={() => addNestedArrayItem('brandStory', 'stats', { value: '', label: '' })} className="w-full border-2 border-dashed border-stone-300 rounded-lg p-4 hover:border-stone-400 hover:bg-stone-50 transition-all">
                        <Plus className="w-5 h-5 mx-auto text-stone-400 mb-1" />
                        <span className="text-stone-600 text-sm">Add Stat</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Testimonial Section (singular) */}
              {activeSection === 'testimonial' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Featured Testimonial</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Highlight a single powerful customer testimonial.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Testimonial Quote</label>
                    <textarea value={formData.testimonial?.quote || ''} onChange={(e) => handleInputChange('testimonial.quote', e.target.value)} placeholder="The customer's testimonial text..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Author Name</label>
                      <input type="text" value={formData.testimonial?.author || ''} onChange={(e) => handleInputChange('testimonial.author', e.target.value)} placeholder="e.g., Sarah Johnson" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Author Title/Role</label>
                      <input type="text" value={formData.testimonial?.title || ''} onChange={(e) => handleInputChange('testimonial.title', e.target.value)} placeholder="e.g., Fashion Blogger" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                </div>
              )}

              {/* Trending Section */}
              {activeSection === 'trending' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-fuchsia-600 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Trending Products</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your trending and popular products.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.trending?.title || ''} onChange={(e) => handleInputChange('trending.title', e.target.value)} placeholder="e.g., Trending Now" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">Trending Items</label>
                    {formData.trending?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-fuchsia-50 to-violet-50 border border-fuchsia-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-fuchsia-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('trending', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <input type="text" value={item.name || ''} onChange={(e) => updateNestedArrayItem('trending', 'items', index, 'name', e.target.value)} placeholder="Product Name" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.price || ''} onChange={(e) => updateNestedArrayItem('trending', 'items', index, 'price', e.target.value)} placeholder="Price (e.g., ₹1,999)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.tag || ''} onChange={(e) => updateNestedArrayItem('trending', 'items', index, 'tag', e.target.value)} placeholder="Tag (e.g., Hot)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                        <FileUploadArea fieldName={`trending_${index}`} label="Product Image" accept="image/*" dimensions="400x500px recommended" existingUrl={item.image || ''} aspectRatio="3:4" />
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('trending', 'items', { name: '', price: '', tag: '', image: '' })} className="w-full border-2 border-dashed border-fuchsia-300 rounded-2xl p-8 hover:border-fuchsia-400 hover:bg-fuchsia-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-fuchsia-400 group-hover:text-fuchsia-600 mb-3" />
                      <span className="text-fuchsia-600 font-medium">Add Trending Item</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Reels Section */}
              {activeSection === 'reels' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-pink-600 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video Reels</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase short-form video content and reels.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.reels?.title || ''} onChange={(e) => handleInputChange('reels.title', e.target.value)} placeholder="e.g., Shop The Reels" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">Reels</label>
                    {formData.reels?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('reels', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <input type="text" value={item.views || ''} onChange={(e) => updateNestedArrayItem('reels', 'items', index, 'views', e.target.value)} placeholder="Views (e.g., 12.5K)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.likes || ''} onChange={(e) => updateNestedArrayItem('reels', 'items', index, 'likes', e.target.value)} placeholder="Likes (e.g., 2.3K)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                        <FileUploadArea fieldName={`reel_${index}`} label="Reel Thumbnail (9:16 aspect ratio)" accept="image/*" dimensions="360x640px recommended" existingUrl={item.image || ''} aspectRatio="9:16" />
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('reels', 'items', { image: '', views: '', likes: '' })} className="w-full border-2 border-dashed border-pink-300 rounded-2xl p-8 hover:border-pink-400 hover:bg-pink-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-pink-400 group-hover:text-pink-600 mb-3" />
                      <span className="text-pink-600 font-medium">Add Reel</span>
                    </button>
                  </div>
                </div>
              )}

              {/* UGC Section */}
              {activeSection === 'ugc' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">User Generated Content</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase content created by your customers.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Hashtag</label>
                      <input type="text" value={formData.ugc?.hashtag || ''} onChange={(e) => handleInputChange('ugc.hashtag', e.target.value)} placeholder="e.g., #YourBrandStyle" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                      <input type="text" value={formData.ugc?.subtitle || ''} onChange={(e) => handleInputChange('ugc.subtitle', e.target.value)} placeholder="e.g., Share your style" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">UGC Images (1:1 aspect ratio)</label>
                    <p className="text-xs text-gray-500 mb-4">Upload square images from your customers</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {formData.ugc?.images?.map((img, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                            {img && <img src={img} alt={`UGC ${index + 1}`} className="w-full h-full object-cover" />}
                          </div>
                          <button onClick={() => {
                            const newImages = [...(formData.ugc?.images || [])];
                            newImages.splice(index, 1);
                            handleInputChange('ugc.images', newImages);
                          }} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <FileUploadArea fieldName="ugc_images" label="Add UGC Image" accept="image/*" dimensions="400x400px" existingUrl="" aspectRatio="1:1" />
                    </div>
                  </div>
                </div>
              )}

              {/* Impact Stats Section */}
              {activeSection === 'impactStats' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Impact Statistics</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your brand's sustainability and impact metrics.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.impactStats?.title || ''} onChange={(e) => handleInputChange('impactStats.title', e.target.value)} placeholder="e.g., Our Impact" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">Impact Stats</label>
                    {formData.impactStats?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('impactStats', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input type="text" value={item.icon || ''} onChange={(e) => updateNestedArrayItem('impactStats', 'items', index, 'icon', e.target.value)} placeholder="Icon name (e.g., Leaf, Droplet)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.value || ''} onChange={(e) => updateNestedArrayItem('impactStats', 'items', index, 'value', e.target.value)} placeholder="Value (e.g., 50%)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.label || ''} onChange={(e) => updateNestedArrayItem('impactStats', 'items', index, 'label', e.target.value)} placeholder="Label (e.g., Water Saved)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.description || ''} onChange={(e) => updateNestedArrayItem('impactStats', 'items', index, 'description', e.target.value)} placeholder="Description" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('impactStats', 'items', { icon: '', value: '', label: '', description: '' })} className="w-full border-2 border-dashed border-emerald-300 rounded-2xl p-8 hover:border-emerald-400 hover:bg-emerald-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-emerald-400 group-hover:text-emerald-600 mb-3" />
                      <span className="text-emerald-600 font-medium">Add Impact Stat</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Mission Section */}
              {activeSection === 'mission' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Mission Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share your brand's mission and purpose.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mission Quote</label>
                    <textarea value={formData.mission?.quote || ''} onChange={(e) => handleInputChange('mission.quote', e.target.value)} placeholder="Your inspiring mission statement..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea value={formData.mission?.description || ''} onChange={(e) => handleInputChange('mission.description', e.target.value)} placeholder="Elaborate on your mission..." rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              )}

              {/* Artisans Section */}
              {activeSection === 'artisans' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Artisans</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the skilled artisans behind your products.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                      <input type="text" value={formData.artisans?.title || ''} onChange={(e) => handleInputChange('artisans.title', e.target.value)} placeholder="e.g., Meet Our Artisans" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <input type="text" value={formData.artisans?.description || ''} onChange={(e) => handleInputChange('artisans.description', e.target.value)} placeholder="Brief description of your artisan community" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">Artisans</label>
                    {formData.artisans?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('artisans', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <input type="text" value={item.name || ''} onChange={(e) => updateNestedArrayItem('artisans', 'items', index, 'name', e.target.value)} placeholder="Artisan Name" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.craft || ''} onChange={(e) => updateNestedArrayItem('artisans', 'items', index, 'craft', e.target.value)} placeholder="Craft (e.g., Hand Weaving)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={item.location || ''} onChange={(e) => updateNestedArrayItem('artisans', 'items', index, 'location', e.target.value)} placeholder="Location" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                        <textarea value={item.story || ''} onChange={(e) => updateNestedArrayItem('artisans', 'items', index, 'story', e.target.value)} placeholder="Artisan's story..." rows={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4" />
                        <FileUploadArea fieldName={`artisan_${index}`} label="Artisan Photo (1:1 aspect ratio)" accept="image/*" dimensions="400x400px recommended" existingUrl={item.image || ''} aspectRatio="1:1" />
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('artisans', 'items', { name: '', craft: '', location: '', story: '', image: '' })} className="w-full border-2 border-dashed border-amber-300 rounded-2xl p-8 hover:border-amber-400 hover:bg-amber-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-amber-400 group-hover:text-amber-600 mb-3" />
                      <span className="text-amber-600 font-medium">Add Artisan</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Transparency Section */}
              {activeSection === 'transparency' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Info className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Transparency</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Share your brand's transparency and ethical practices.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.transparency?.title || ''} onChange={(e) => handleInputChange('transparency.title', e.target.value)} placeholder="e.g., Our Transparency Promise" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea value={formData.transparency?.description || ''} onChange={(e) => handleInputChange('transparency.description', e.target.value)} placeholder="Describe your transparency practices..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text</label>
                    <input type="text" value={formData.transparency?.ctaText || ''} onChange={(e) => handleInputChange('transparency.ctaText', e.target.value)} placeholder="e.g., Learn More About Our Practices" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              )}

              {/* Certifications Section */}
              {activeSection === 'certifications' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-lime-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Certifications</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Display your brand's certifications and credentials.</p>
                  </div>
                  <div className="space-y-4">
                    {formData.certifications?.map((cert, index) => (
                      <div key={index} className="bg-gradient-to-br from-green-50 to-lime-50 border border-green-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeArrayItem('certifications', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <input type="text" value={cert.name || ''} onChange={(e) => updateArrayItem('certifications', index, 'name', e.target.value)} placeholder="Certification Name (e.g., GOTS Certified)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={cert.description || ''} onChange={(e) => updateArrayItem('certifications', index, 'description', e.target.value)} placeholder="Description" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('certifications', { name: '', description: '' })} className="w-full border-2 border-dashed border-green-300 rounded-2xl p-8 hover:border-green-400 hover:bg-green-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-green-400 group-hover:text-green-600 mb-3" />
                      <span className="text-green-600 font-medium">Add Certification</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Featured Image Section */}
              {activeSection === 'featuredImage' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Featured Image</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">A prominent featured image for your homepage.</p>
                  </div>
                  <FileUploadArea fieldName="featuredImage" label="Featured Image (21:9 aspect ratio recommended)" accept="image/*" dimensions="1920x820px recommended" existingUrl={formData.featuredImage?.src || ''} aspectRatio="21:9" />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Alt Text</label>
                    <input type="text" value={formData.featuredImage?.alt || ''} onChange={(e) => handleInputChange('featuredImage.alt', e.target.value)} placeholder="Describe the image for accessibility" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              )}

              {/* Materials Section */}
              {activeSection === 'materials' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-stone-600 to-stone-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Layers className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Materials</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the premium materials used in your products.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.materials?.title || ''} onChange={(e) => handleInputChange('materials.title', e.target.value)} placeholder="e.g., Premium Materials" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea value={formData.materials?.description || ''} onChange={(e) => handleInputChange('materials.description', e.target.value)} placeholder="Describe your material sourcing and quality..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FileUploadArea fieldName="materials_image1" label="Material Image 1 (1:1 aspect ratio)" accept="image/*" dimensions="600x600px recommended" existingUrl={formData.materials?.image1 || ''} aspectRatio="1:1" />
                    <FileUploadArea fieldName="materials_image2" label="Material Image 2 (1:1 aspect ratio)" accept="image/*" dimensions="600x600px recommended" existingUrl={formData.materials?.image2 || ''} aspectRatio="1:1" />
                  </div>
                </div>
              )}

              {/* Style Guide Section */}
              {activeSection === 'styleGuide' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-slate-600 to-blue-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Style Guide</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Help customers style your products with curated looks.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.styleGuide?.title || ''} onChange={(e) => handleInputChange('styleGuide.title', e.target.value)} placeholder="e.g., Style Guide" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">Style Tips</label>
                    {formData.styleGuide?.items?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-slate-700 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeNestedArrayItem('styleGuide', 'items', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <input type="text" value={item.title || ''} onChange={(e) => updateNestedArrayItem('styleGuide', 'items', index, 'title', e.target.value)} placeholder="Style Tip Title" className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4" />
                        <textarea value={item.description || ''} onChange={(e) => updateNestedArrayItem('styleGuide', 'items', index, 'description', e.target.value)} placeholder="Style tip description..." rows={2} className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4" />
                        <FileUploadArea fieldName={`styleGuide_${index}`} label="Style Image (4:5 aspect ratio)" accept="image/*" dimensions="400x500px recommended" existingUrl={item.image || ''} aspectRatio="4:5" />
                      </div>
                    ))}
                    <button onClick={() => addNestedArrayItem('styleGuide', 'items', { title: '', description: '', image: '' })} className="w-full border-2 border-dashed border-slate-300 rounded-2xl p-8 hover:border-slate-400 hover:bg-slate-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-slate-400 group-hover:text-slate-600 mb-3" />
                      <span className="text-slate-600 font-medium">Add Style Tip</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Bespoke Services Section */}
              {activeSection === 'bespoke' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Bespoke Services</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Highlight your custom and personalized services.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input type="text" value={formData.bespoke?.title || ''} onChange={(e) => handleInputChange('bespoke.title', e.target.value)} placeholder="e.g., Bespoke Services" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea value={formData.bespoke?.description || ''} onChange={(e) => handleInputChange('bespoke.description', e.target.value)} placeholder="Describe your bespoke services..." rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text</label>
                    <input type="text" value={formData.bespoke?.ctaText || ''} onChange={(e) => handleInputChange('bespoke.ctaText', e.target.value)} placeholder="e.g., Book a Consultation" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              )}

              {/* Promo Banners Section */}
              {activeSection === 'promoBanners' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Promo Banners</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create promotional banners for your campaigns.</p>
                  </div>
                  <div className="space-y-4">
                    {formData.promoBanners?.map((banner, index) => (
                      <div key={index} className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-2xl p-6">
                        <div className="flex justify-between items-center mb-4">
                          <span className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center text-sm font-bold">{index + 1}</span>
                          <button onClick={() => removeArrayItem('promoBanners', index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <input type="text" value={banner.title || ''} onChange={(e) => updateArrayItem('promoBanners', index, 'title', e.target.value)} placeholder="Banner Title" className="px-4 py-3 border border-gray-300 rounded-lg" />
                          <input type="text" value={banner.ctaText || ''} onChange={(e) => updateArrayItem('promoBanners', index, 'ctaText', e.target.value)} placeholder="CTA Text (e.g., Shop Now)" className="px-4 py-3 border border-gray-300 rounded-lg" />
                        </div>
                        <FileUploadArea fieldName={`promoBanner_${index}`} label="Banner Image (2:1 aspect ratio)" accept="image/*" dimensions="800x400px recommended" existingUrl={banner.image || ''} aspectRatio="2:1" />
                      </div>
                    ))}
                    <button onClick={() => addArrayItem('promoBanners', { image: '', title: '', ctaText: '' })} className="w-full border-2 border-dashed border-rose-300 rounded-2xl p-8 hover:border-rose-400 hover:bg-rose-50 transition-all group">
                      <Plus className="w-8 h-8 mx-auto text-rose-400 group-hover:text-rose-600 mb-3" />
                      <span className="text-rose-600 font-medium">Add Promo Banner</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Default/Fallback Section - Shows when section doesn't have specific form */}
              {!['brand', 'hero', 'categories', 'collections', 'videos', 'business', 'statement', 'workshop',
                 'fashion_trends', 'gallery', 'instagram', 'about', 'lookbook', 'featured', 'brand_story',
                 'video', 'luxury_statement', 'prestige_services', 'craftsmanship', 'human_connection',
                 'brand_statement', 'featured_stories', 'quote', 'portfolio', 'artist_statement',
                 'announcement', 'promo', 'marquee', 'products', 'intro', 'testimonials',
                 'newsletter', 'brandStatement', 'philosophy', 'editorial', 'features',
                 'services', 'servicesBar', 'athlete', 'brandValues', 'flashSale',
                 'videoBanner', 'videoCampaign', 'videoLookbook', 'reviews', 'theEdit',
                 'featuredBrands', 'trustBanner', 'consultation', 'stores', 'cityCollections',
                 'behindScenes', 'designers', 'brandStory', 'testimonial', 'trending',
                 'reels', 'ugc', 'impactStats', 'mission', 'artisans', 'transparency',
                 'certifications', 'featuredImage', 'materials', 'styleGuide', 'bespoke',
                 'promoBanners'].includes(activeSection) && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3 capitalize">
                      {SECTION_METADATA[activeSection]?.label || activeSection.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                    </h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Configure the {activeSection.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toLowerCase()} section for your template.
                    </p>
                  </div>

                  {/* Dynamic form fields based on formData structure */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Title field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                        <input
                          type="text"
                          value={formData[activeSection]?.title || formData[activeSection]?.label || ''}
                          onChange={(e) => handleInputChange(`${activeSection}.title`, e.target.value)}
                          placeholder={`Enter ${activeSection} title`}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Subtitle/Description field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                        <input
                          type="text"
                          value={formData[activeSection]?.subtitle || formData[activeSection]?.description || ''}
                          onChange={(e) => handleInputChange(`${activeSection}.subtitle`, e.target.value)}
                          placeholder={`Enter ${activeSection} subtitle`}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Description textarea */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={formData[activeSection]?.description || formData[activeSection]?.text || ''}
                        onChange={(e) => handleInputChange(`${activeSection}.description`, e.target.value)}
                        placeholder={`Enter a description for this section...`}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* CTA fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                        <input
                          type="text"
                          value={formData[activeSection]?.ctaText || formData[activeSection]?.buttonText || ''}
                          onChange={(e) => handleInputChange(`${activeSection}.ctaText`, e.target.value)}
                          placeholder="e.g., Learn More"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Button Link</label>
                        <input
                          type="text"
                          value={formData[activeSection]?.ctaLink || formData[activeSection]?.link || ''}
                          onChange={(e) => handleInputChange(`${activeSection}.ctaLink`, e.target.value)}
                          placeholder="e.g., /about"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Image upload */}
                    <div className="mt-6">
                      <FileUploadArea
                        fieldName={`${activeSection}_image`}
                        label="Section Image (optional)"
                        accept="image/*"
                        dimensions="800x600px recommended"
                        description="Upload an image for this section"
                        existingUrl={formData[activeSection]?.image || formData[activeSection]?.backgroundImage || ''}
                        aspectRatio="4:3"
                      />
                    </div>
                  </div>

                  {/* Info message */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-blue-800 font-medium">Section Configuration</p>
                      <p className="text-blue-600 text-sm mt-1">
                        This section will be populated with your content. The template's design will automatically style it according to its theme.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {sections.length > 1 && (
                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection);
                      if (currentIndex > 0) {
                        setActiveSection(sections[currentIndex - 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Previous Section</span>
                  </button>

                  <div className="text-sm text-gray-600 font-medium">
                    Section {sections.findIndex(s => s.id === activeSection) + 1} of {sections.length}
                  </div>

                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection);
                      if (currentIndex < sections.length - 1) {
                        setActiveSection(sections[currentIndex + 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === sections.length - 1}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="font-medium">
                      {sections.findIndex(s => s.id === activeSection) === sections.length - 1 ? 'Last Section' : 'Next Section'}
                    </span>
                    {sections.findIndex(s => s.id === activeSection) < sections.length - 1 ? (
                      <ArrowRight className="w-5 h-5" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default MainPageForm;
